/* eslint-disable */
import { useLocation } from "react-router-dom";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { removeBackground } from "@imgly/background-removal";
import axios from "axios";
import Joyride, { STATUS } from "react-joyride";
import { MDBContainer, MDBBtn } from "mdb-react-ui-kit";
import { usePageState } from "../../context/AppStateContext";
import { useAuth } from "../../context/AuthContext";
import { applyDisplayPrice } from "../../utils/displayPrice";
import {
  resolveConditionScore,
  isConditionAcceptable,
} from "../../utils/conditionScore";
import { normalizeRole } from "../../utils/rolePermissions";
import "./AddProductWizard.css";

import AutoFillLoader from "../Common/Loaders/AutoFillLoader";
import SubmissionLoader from "../Common/Loaders/SubmissionLoader";
import { useMessageBox } from "../Common/MessageBox";

import ImageUploadStep from "./ImageUploadStep";
import ListingDetailsStep from "./ListingDetailsStep";
import MarketplacesStep from "./MarketplacesStep";
import ImageProcessingStep from "./ImageProcessingStep";
import ReviewStep from "./ReviewStep";

import {
  steps,
  autoFillMessages,
  blurMessages,
  poshmarkSubCategoriesMap,
  marketplaces,
} from "./constants";
import {
  buildAddProductAppStateSnapshot,
  buildCreateProductPayload,
  buildEbayAspects,
  buildMetafieldsFromAnalysis,
  ensureHostedImageUrls,
  filterPersistableImageUrls,
  isHostedImageUrl,
  uploadFilesToERP,
} from "./helpers";
import { createProductFlowLogger } from "../../services/productFlowLogger";

const AddProduct = () => {
  const { state: persistedState, setState: setPersistedState } =
    usePageState("addProduct");
  const { showError, showSuccess, showWarning, MessageBoxComponent } =
    useMessageBox();
  const { displayPricePercentage, minimumAcceptableCondition, user } =
    useAuth();
  const isSeller = normalizeRole(user?.role) === "seller";
  const isOwnerOrAdmin = ["owner", "admin"].includes(normalizeRole(user?.role));

  const [currentStep, setCurrentStep] = useState(
    () => persistedState.currentStep || 1,
  );
  const [processingImages, setProcessingImages] = useState(false);
  const [progress, setProgress] = useState(0);
  const [blurTextIndex, setBlurTextIndex] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [run, setRun] = useState(false);
  const [activeMetafields, setActiveMetafields] = useState([]);
  const [loadingMetafields, setLoadingMetafields] = useState(false);
  const companyId = localStorage.getItem("company_id");
  const productFlowLoggerRef = useRef(null);

  const [poshmarkConnected, setPoshmarkConnected] = useState(false);
  const [checkingPoshmark, setCheckingPoshmark] = useState(false);
  const [poshmarkSubCategories, setPoshmarkSubCategories] = useState([]);

  const [ebayConnected, setEbayConnected] = useState(false);
  const [checkingEbay, setCheckingEbay] = useState(false);

  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [autoFillMessageIndex, setAutoFillMessageIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStep, setSubmissionStep] = useState("");
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [autoFillResponse, setAutoFillResponse] = useState(null);

  const [savedEbayPolicies, setSavedEbayPolicies] = useState(null);

  const [cloneApplied, setCloneApplied] = useState(false);
  const BASE_URL =
    "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

  const getUserIdFromStorage = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user.id || user.user_id || user.userId || null;
    } catch (error) {
      return null;
    }
  };

  const getProductFlowLogger = () => {
    if (productFlowLoggerRef.current) {
      return productFlowLoggerRef.current;
    }

    if (!companyId) return null;

    productFlowLoggerRef.current = createProductFlowLogger({
      companyId,
      userId: getUserIdFromStorage(),
    });

    return productFlowLoggerRef.current;
  };

  const startStepTimer = (step) => getProductFlowLogger()?.startTimer(step);

  const endStepTimer = async (timer, metadata) => {
    const productFlowLogger = getProductFlowLogger();
    if (!productFlowLogger || !timer) return;
    await productFlowLogger.endTimer(timer, metadata);
  };

  const safeLogError = async (step, error, metadata = {}) => {
    const productFlowLogger = getProductFlowLogger();
    if (!productFlowLogger) return;

    await productFlowLogger.logError({
      step,
      error_message: error?.message || "Unknown error",
      stack_trace: error?.stack || "",
      request_payload: metadata.request_payload || null,
      response_payload: metadata.response_payload || null,
    });
  };

  const [processedImages, setProcessedImages] = useState(() =>
    filterPersistableImageUrls(persistedState.processedImages || []),
  );

  const [formData, setFormData] = useState(() => ({
    uploadedImages: filterPersistableImageUrls(
      persistedState.formData?.uploadedImages || [],
    ),
    categoryAspects: persistedState.formData?.categoryAspects || [],
    allCategoryAspects: persistedState.formData?.allCategoryAspects || [],
    categoryId: persistedState.formData?.categoryId || "",
    blurStatuses: persistedState.formData?.blurStatuses || [],
    imageProcessing: false,
    backgroundRemoval: persistedState.formData?.backgroundRemoval || false,
    blurDetection: persistedState.formData?.blurDetection || false,
    upscaleImage: persistedState.formData?.upscaleImage || false,

    title: persistedState.formData?.title || "",
    description: persistedState.formData?.description || "",
    sku: persistedState.formData?.sku || "",
    price: persistedState.formData?.price || "",
    listingType: persistedState.formData?.listingType || "FIXED_PRICE",
    pricerange: persistedState.formData?.pricerange || "",
    quantity: persistedState.formData?.quantity || "1",
    brand: persistedState.formData?.brand || "",
    color: persistedState.formData?.color || "",
    category: persistedState.formData?.category || "",
    condition: persistedState.formData?.condition || "",
    condition_score: persistedState.formData?.condition_score ?? "",
    condition_summary: persistedState.formData?.condition_summary || "",
    authenticity_score: persistedState.formData?.authenticity_score || "",
    authenticity_summary: persistedState.formData?.authenticity_summary || "",
    material: persistedState.formData?.material || "",
    tags: persistedState.formData?.tags || "",
    metafields: persistedState.formData?.metafields || {},

    variants: persistedState.formData?.variants || [
      { id: 1, size: "", color: "", sku: "" },
    ],

    selectedMarketplaces: persistedState.formData?.selectedMarketplaces || [],

    poshmarkCategory: persistedState.formData?.poshmarkCategory || "Women",
    poshmarkSubCategory: persistedState.formData?.poshmarkSubCategory || "",
    poshmarkSizeType: persistedState.formData?.poshmarkSizeType || "standard",
    poshmarkAvailability:
      persistedState.formData?.poshmarkAvailability || "available",

    vintedCategory: persistedState.formData?.vintedCategory || "",
    vintedCategoryId: persistedState.formData?.vintedCategoryId || "",
    vintedCategoryPath: persistedState.formData?.vintedCategoryPath || [],
    vintedPackageSize: persistedState.formData?.vintedPackageSize || "medium",
    vintedSize: persistedState.formData?.vintedSize || "",

    allsuggestedCategories:
      persistedState.formData?.allsuggestedCategories || [],
        vintedDescription: persistedState.formData?.vintedDescription || "",

  }));

  const location = useLocation();
  // ========== CLONE PRODUCT PREFILL USING /api/product-full/:id ==========
  // ========== CLONE PRODUCT PREFILL USING /api/product-full/:id ==========
  // ========== CLONE PRODUCT PREFILL USING /api/product-full/:id ==========
  useEffect(() => {
    const fetchAndPrefill = async () => {
      if (cloneApplied) return;
      const cloneData = location.state?.cloneProduct;
      if (!cloneData) {
        console.log("❌ No cloneData found");
        return;
      }

      const productId = cloneData.id;
      if (!productId) {
        console.log("❌ cloneData.id missing", cloneData);
        return;
      }

      console.log("🔍 Fetching product-full for ID:", productId);
try {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BASE_URL}/api/product-full/${productId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await res.json();
  console.log("📦 API response:", json);

  if (!json.success || !json.data) {
    console.error("API returned error", json);
    return;
  }

  const { product, ebay } = json.data;
  if (!product) return;

  // ========== 1. Extract saved formData from raw_response ==========
  let savedFormData = {};
  if (product.raw_response) {
    try {
      let raw = product.raw_response;
      if (typeof raw === "string") raw = JSON.parse(raw);
      if (raw.formData) {
        savedFormData = raw.formData;
        console.log("📦 Extracted saved formData from raw_response:", savedFormData);
      }
    } catch (err) {
      console.warn("Failed to parse raw_response", err);
    }
  }

  // ========== 2. Extract eBay aspects (if any) ==========
  const ebayFields = {};
  if (ebay && ebay.aspects) {
    let aspects = ebay.aspects;
    if (typeof aspects === "string") aspects = JSON.parse(aspects);
    console.log("🎯 eBay aspects (raw):", aspects);

    Object.entries(aspects).forEach(([key, value]) => {
      let val = Array.isArray(value) ? value.join(", ") : value;
      if (val && val !== "N/A" && val.trim() !== "") {
        let mappedKey = key;
        if (key === "Marke") mappedKey = "Brand";
        else if (key === "Farbe") mappedKey = "Color";
        else if (key === "Größe") mappedKey = "Size";
        else if (key === "Material") mappedKey = "Material";
        else if (key === "Zustand") mappedKey = "Condition";
        else if (key === "Produkttyp") mappedKey = "ProductType";
        else if (key === "Type") mappedKey = "ProductType";
        else if (key === "Colour") mappedKey = "Color";
        else if (key === "Condition") mappedKey = "Condition";
        else if (key === "Brand") mappedKey = "Brand";
        ebayFields[mappedKey] = val;
      }
    });
  }

  if (ebay && ebay.condition && !ebayFields.Condition) {
    ebayFields.Condition = ebay.condition;
  }
  console.log("✅ All eBay fields after mapping:", ebayFields);

  // ========== 3. Merge values (eBay > savedFormData > product) ==========
  const finalBrand = ebayFields.Brand || savedFormData.brand || product.brand || "";
  const finalColor = ebayFields.Color || savedFormData.color || product.color || "";
  const finalMaterial = ebayFields.Material || savedFormData.material || product.material || "";
  const finalCategory = ebayFields.ProductType || savedFormData.category || product.category || product.product_type || "";
  const finalCondition = ebayFields.Condition || savedFormData.condition || product.condition || "";
  const finalSize = ebayFields.Size || savedFormData.size || product.size || "";
  const finalPrice = ebayFields.Price || savedFormData.price || product.price?.toString() || "";
  const finalTags = savedFormData.tags || product.tags || "";
  const finalVariants = (savedFormData.variants && savedFormData.variants.length)
    ? savedFormData.variants
    : [{ id: 1, size: finalSize, color: finalColor, sku: product.sku || "" }];
  const finalUploadedImages = (product.images && product.images.length) ? product.images : (savedFormData.uploadedImages || []);
  const finalSelectedMarketplaces = (cloneData.selectedMarketplaces || savedFormData.selectedMarketplaces || []).filter(m => m !== "local");
  const finalCategoryAspects = savedFormData.categoryAspects || [];
  const finalAllCategoryAspects = savedFormData.allCategoryAspects || [];
  const finalCategoryId = savedFormData.categoryId || "";
  const finalConditionScore = savedFormData.condition_score || "";
  const finalConditionSummary = savedFormData.condition_summary || "";
  const finalAuthenticityScore = savedFormData.authenticity_score || "";
  const finalAuthenticitySummary = savedFormData.authenticity_summary || "";
  const finalPoshmarkCategory = savedFormData.poshmarkCategory || "Women";
  const finalPoshmarkSubCategory = savedFormData.poshmarkSubCategory || "";
  const finalVintedCategory = savedFormData.vintedCategory || "";
  const finalVintedCategoryId = savedFormData.vintedCategoryId || "";
  const finalVintedPackageSize = savedFormData.vintedPackageSize || "medium";
  const finalProductType = ebayFields.ProductType || savedFormData.productType || "";

  const prefillData = {
    title: product.title || "",
    description: product.description || "",
    sku: product.sku || "",
    price: finalPrice,
    quantity: product.quantity?.toString() || "1",
    category: finalCategory,
    productType: finalProductType,
    condition: finalCondition,
    uploadedImages: finalUploadedImages,
    blurStatuses: Array(finalUploadedImages.length).fill(false),
    selectedMarketplaces: finalSelectedMarketplaces,
    brand: finalBrand,
    color: finalColor,
    material: finalMaterial,
    tags: finalTags,
    variants: finalVariants,
    vintedSize: finalSize,
    // eBay aspects
    categoryAspects: finalCategoryAspects,
    allCategoryAspects: finalAllCategoryAspects,
    categoryId: finalCategoryId,
    condition_score: finalConditionScore,
    condition_summary: finalConditionSummary,
    authenticity_score: finalAuthenticityScore,
    authenticity_summary: finalAuthenticitySummary,
    // Poshmark & Vinted
    poshmarkCategory: finalPoshmarkCategory,
    poshmarkSubCategory: finalPoshmarkSubCategory,
    vintedCategory: finalVintedCategory,
    vintedCategoryId: finalVintedCategoryId,
    vintedPackageSize: finalVintedPackageSize,
  };

  console.log("🔄 Complete Prefill data being set (with raw_response):", prefillData);

  setFormData((prev) => ({ ...prev, ...prefillData }));
  setCurrentStep(2);
  setCloneApplied(true);
} catch (err) {
  console.error("❌ Error fetching product-full:", err);
  // Fallback: use cloneData as is (basic fields)
  const fallbackData = {
    title: cloneData.title || "",
    description: cloneData.description || "",
    sku: cloneData.sku || "",
    price: cloneData.price?.toString() || "",
    quantity: cloneData.quantity?.toString() || "1",
    category: cloneData.category || "",
    condition: cloneData.condition || "",
    uploadedImages: Array.isArray(cloneData.images) ? cloneData.images : [],
    blurStatuses: Array(Array.isArray(cloneData.images) ? cloneData.images.length : 0).fill(false),
    selectedMarketplaces: (cloneData.selectedMarketplaces || []).filter(m => m !== "local"),
  };
  setFormData((prev) => ({ ...prev, ...fallbackData }));
  setCurrentStep(2);
  setCloneApplied(true);
}   };

    fetchAndPrefill();
  }, [location.state, cloneApplied]);

  useEffect(() => {
    const { imageProcessing, ...persistableFormData } = formData;
    setPersistedState({
      formData: {
        ...persistableFormData,
            vintedDescription: formData.vintedDescription,

        uploadedImages: filterPersistableImageUrls(
          persistableFormData.uploadedImages,
        ),
      },
      currentStep,
      processedImages: filterPersistableImageUrls(processedImages),
    });
  }, [formData, currentStep, processedImages, setPersistedState]);
  // Step 4 pe automatically _pi images load karo
  // Naya state add karo
  const [editedImages, setEditedImages] = useState([]);

  // Step 4 useEffect mein
  // Step 4 useEffect mein — setEditedImages use karo, setProcessedImages nahi
  useEffect(() => {
    if (currentStep === 4 && formData.uploadedImages.length > 0) {
      const fetchEditedImages = async () => {
        try {
          const res = await fetch(
            "https://erp.thriftops.com/Upload/getEditedImages.php",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ images: formData.uploadedImages }),
            },
          );
          const data = await res.json();
          if (data.success) {
            // ✅ editedImages mein daalo — processedImages mein nahi
            setEditedImages(data.images.map((img) => img.edited));
            // ✅ processedImages reset karo taake Processed section na dikhe
            setProcessedImages([]);
          }
        } catch (err) {
          console.error("getEditedImages failed:", err);
        }
      };
      fetchEditedImages();
    }
  }, [currentStep]);
  const getFormConditionScore = useCallback(() => {
    return resolveConditionScore(formData.condition_score, formData.condition);
  }, [formData.condition_score, formData.condition]);

  const isProductConditionAcceptable = useCallback(() => {
    return isConditionAcceptable(
      getFormConditionScore(),
      minimumAcceptableCondition,
    );
  }, [getFormConditionScore, minimumAcceptableCondition]);

  useEffect(() => {
    setRun(true);
  }, []);

  const stepstofollow = [
    {
      target: "#image-upload",
      content: "Upload your product images",
      disableBeacon: true,
      hideFooter: true,
      disableOverlayClose: true,
    },
  ];

  const fetchActiveMetafields = async () => {
    setLoadingMetafields(true);
    try {
      const response = await fetch(
        `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getActiveShopifyMetafields/${companyId}`,
      );
      const result = await response.json();
      if (result.success) {
        console.log(result);
        setActiveMetafields(result.data.active_metafields);
      }
    } catch (err) {
      console.error("API fetch error:", err);
    } finally {
      setLoadingMetafields(false);
    }
  };

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if (status === STATUS.FINISHED) {
      setRun(false);
    }
  };

  const moveNext = () => {
    setStepIndex((prev) => prev + 1);
  };

  useEffect(() => {
    fetchActiveMetafields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check eBay store connection status
  useEffect(() => {
    const checkEbayStore = async () => {
      if (!formData.selectedMarketplaces.includes("ebay")) {
        setEbayConnected(false);
        return;
      }

      const companyId = localStorage.getItem("company_id");
      console.log(localStorage);
      if (!companyId) {
        setEbayConnected(false);
        return;
      }

      try {
        setCheckingEbay(true);
        const res = await fetch(
          `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getEbayStoreByCompany/${companyId}`,
        );
        const data = await res.json();

        if (res.ok && data.success && data.data) {
          setEbayConnected(true);
        } else {
          setEbayConnected(false);
        }
      } catch (err) {
        console.error("eBay store check error:", err);
        setEbayConnected(false);
      } finally {
        setCheckingEbay(false);
      }
    };

    checkEbayStore();
  }, [formData.selectedMarketplaces]);

  // Fetch product settings (min_images, file_size_kb, size_chart_required)

  useEffect(() => {
    const fetchProductSettings = async () => {
      if (!companyId) return;
      try {
        const token = localStorage.getItem("token"); // 👈 token yahan se lo
        const res = await fetch(
          `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/general-settings/${companyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`, // 👈 ye important hai
              "Content-Type": "application/json",
            },
          },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setProductSettings({
          minImages: data.min_images ?? 0,
          fileSizeKb: data.file_size_kb ?? 5120,
          sizeChartRequired: data.size_chart_required || "not_required",
        });
        setSettingsLoaded(true);
      } catch (err) {
        console.error("Failed to fetch product settings:", err);
        setProductSettings({
          minImages: 0,
          fileSizeKb: 5120,
          sizeChartRequired: "not_required",
        });
        setSettingsLoaded(true);
      }
    };
    fetchProductSettings();
  }, [companyId]);

  useEffect(() => {
    if (!formData.imageProcessing) return;
    const interval = setInterval(() => {
      setBlurTextIndex((prev) => (prev + 1) % blurMessages.length);
    }, 1500);
    return () => clearInterval(interval);
  }, [formData.imageProcessing]);

  useEffect(() => {
    if (!isAutoFilling) return;
    const interval = setInterval(() => {
      setAutoFillMessageIndex((prev) => (prev + 1) % autoFillMessages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [isAutoFilling, autoFillMessages.length]);

  useEffect(() => {
    const checkPoshmarkStatus = async () => {
      if (!formData.selectedMarketplaces.includes("poshmark")) {
        setPoshmarkConnected(false);
        return;
      }
      setCheckingPoshmark(true);
      try {
        const res = await fetch(
          "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/extension-status",
        );
        const data = await res.json();
        setPoshmarkConnected(data.connected);
        if (data.connected) {
          console.log("Poshmark extension connected");
        }
      } catch (err) {
        console.error("Poshmark status check failed:", err);
        setPoshmarkConnected(false);
      } finally {
        setCheckingPoshmark(false);
      }
    };
    checkPoshmarkStatus();
    const interval = setInterval(checkPoshmarkStatus, 30000);
    return () => clearInterval(interval);
  }, [formData.selectedMarketplaces]);

  const handlePoshmarkIntegrate = async (e) => {
    e.stopPropagation();
    try {
      setCheckingPoshmark(true);
      const statusRes = await fetch(
        "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/extension-status",
      );
      const statusData = await statusRes.json();
      if (!statusData.connected) {
        showWarning(
          "Please open Poshmark website in Chrome and refresh the page!",
        );
        window.open("https://poshmark.com/create-listing", "_blank");
      } else {
        showSuccess("Poshmark extension is connected and ready!");
        setPoshmarkConnected(true);
      }
    } catch (err) {
      console.error("Poshmark integration error:", err);
      showError("Failed to check Poshmark extension status");
    } finally {
      setCheckingPoshmark(false);
    }
  };

  const handlePoshmarkCategoryChange = (category) => {
    handleInputChange("poshmarkCategory", category);
    handleInputChange("poshmarkSubCategory", "");
    if (category && poshmarkSubCategoriesMap[category]) {
      setPoshmarkSubCategories(poshmarkSubCategoriesMap[category]);
    } else {
      setPoshmarkSubCategories([]);
    }
  };

  const uploadToPoshmark = async () => {
    setSubmissionStep("Uploading to Poshmark...");
    const poshmarkApiTimer = startStepTimer("CROSS_LISTING_POSHMARK");
    let poshmarkApiStatus = "SUCCESS";
    try {
      const productFlowLogger = getProductFlowLogger();
      const healthCheck = await fetch(
        "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/extension-status",
      );
      const healthData = await healthCheck.json();
      if (productFlowLogger) {
        await productFlowLogger.logApiCall({
          company_id: companyId,
          service_name: "POSHMARK",
          external_service: "POSHMARK",
          endpoint: "/api/extension-status",
          method: "GET",
          request: null,
          response: healthData,
          status_code: healthCheck.status,
          duration_ms: null,
          step: "CROSS_LISTING",
        });
      }
      if (!healthData.connected) {
        throw new Error(
          "Poshmark extension not connected. Please open Poshmark tab and refresh.",
        );
      }
      const files = formData.uploadedImages;
      const imagesBase64 = await Promise.all(
        files.map(async (url) => {
          const res = await fetch(url);
          const blob = await res.blob();
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.readAsDataURL(blob);
          });
        }),
      );
      if (imagesBase64.length === 0) {
        throw new Error("No valid images to upload");
      }
      const conditionMap = {
        new: "New With Tags",
        "like-new": "Like New",
        excellent: "Like New",
        good: "Like New",
        fair: "Like New",
        poor: "Like New",
      };
      const poshmarkPayload = {
        title: formData.title || "Product Listing",
        description: formData.description || "No description provided",
        price: parseFloat(formData.price || "1.00").toString(),
        category: formData.poshmarkCategory || "Women",
        subCategory: formData.poshmarkSubCategory || "Dresses",
        brand: formData.brand || "",
        sizeType: formData.poshmarkSizeType || "standard",
        size: formData.variants?.[0]?.size || "OS",
        condition: conditionMap[formData.condition] || "New With Tags",
        originalPrice: formData.pricerange?.split(" - ")[1] || "",
        color: formData.color || "",
        availability: formData.poshmarkAvailability || "available",
        images: imagesBase64,
      };
      console.log("Poshmark Payload:", poshmarkPayload);
      const poshmarkRes = await fetch(
        "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/start-automation",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(poshmarkPayload),
        },
      );
      const poshmarkData = await poshmarkRes.json();
      if (productFlowLogger) {
        await productFlowLogger.logApiCall({
          company_id: companyId,
          service_name: "POSHMARK",
          external_service: "POSHMARK",
          endpoint: "/api/start-automation",
          method: "POST",
          request: poshmarkPayload,
          response: poshmarkData,
          status_code: poshmarkRes.status,
          duration_ms: null,
          step: "CROSS_LISTING",
        });
      }
      console.log("Poshmark Response:", poshmarkData);
      if (poshmarkRes.ok && poshmarkData.success) {
        return {
          success: true,
          data: poshmarkData,
          message: "Poshmark automation started! Check your browser.",
        };
      } else {
        throw new Error(poshmarkData.error || "Poshmark upload failed");
      }
    } catch (err) {
      console.error("Poshmark Error:", err);
      poshmarkApiStatus = "FAILED";
      await safeLogError("CROSS_LISTING_POSHMARK", err);
      return {
        success: false,
        error: err.message,
      };
    } finally {
      await endStepTimer(poshmarkApiTimer, {
        step: "CROSS_LISTING",
        operation: "POSHMARK_API_FLOW",
        service_name: "POSHMARK",
        status: poshmarkApiStatus,
      });
    }
  };

  const [processing, setProcessing] = useState(false);

  const [vintedMainCategories, setVintedMainCategories] = useState([]);
  const [vintedSubCategories, setVintedSubCategories] = useState([]);
  const [vintedCategoryHistory, setVintedCategoryHistory] = useState([]);
  const [vintedCurrentCategoryId, setVintedCurrentCategoryId] = useState(null);
  const [vintedSelectedCategoryPath, setVintedSelectedCategoryPath] = useState(
    [],
  );

  // Ensure every image is a hosted ERP URL (order preserved)
  const uploadImagesToERP = async (imageUrls) => {
    if (!Array.isArray(imageUrls) || imageUrls.length === 0) return [];

    const uploadTimer = startStepTimer("UPLOAD");
    let uploadStatus = "SUCCESS";
    try {
      return await ensureHostedImageUrls(imageUrls);
    } catch (err) {
      uploadStatus = "FAILED";
      console.error("ERP image upload failed:", err);
      return filterPersistableImageUrls(imageUrls);
    } finally {
      await endStepTimer(uploadTimer, {
        step: "UPLOAD",
        operation: "UPLOAD_TO_ERP",
        service_name: "ADD_PRODUCT_PAGE",
        status: uploadStatus,
      });
    }
  };

  // Helper function - isko useState ke baad add karo
  // ✅ FIX — priority: processedImages > editedImages > uploadedImages
  const getImagesToSubmit = () => {
    if (processedImages && processedImages.length > 0) {
      return processedImages;
    }
    if (editedImages && editedImages.length > 0) {
      return editedImages;
    }
    return formData.uploadedImages;
  };

  const getAddProductRawResponse = () =>
    buildAddProductAppStateSnapshot({
      currentStep,
      formData,
      processedImages,
    });

  const prepareImagesForDatabase = async () => {
    const imagesToSubmit = getImagesToSubmit();
    let imagesForDatabase = [];

    if (imagesToSubmit?.length > 0) {
      try {
        imagesForDatabase = await uploadImagesToERP(imagesToSubmit);
        if (!imagesForDatabase?.length) {
          imagesForDatabase = imagesToSubmit.filter((url) =>
            isHostedImageUrl(url),
          );
        }
      } catch (uploadError) {
        console.error("ERP upload failed:", uploadError);
        imagesForDatabase = imagesToSubmit.filter((url) =>
          isHostedImageUrl(url),
        );
      }
    }

    const validImageUrls = imagesForDatabase.filter(
      (url) => url && typeof url === "string" && isHostedImageUrl(url),
    );

    if (validImageUrls.length === 0) {
      validImageUrls.push("https://via.placeholder.com/500");
    }

    return validImageUrls;
  };

  const saveProductToDatabase = async (status) => {
    const companyId = localStorage.getItem("company_id");
    const validImageUrls = await prepareImagesForDatabase();
    const rawResponse = getAddProductRawResponse();

    const createProductRes = await fetch(`${BASE_URL}/api/create-product`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        buildCreateProductPayload({
          companyId,
          formData,
          images: validImageUrls,
          uploadedBy: getUserIdFromStorage(),
          status,
          rawResponse,
        }),
      ),
    });

    const createProductData = await createProductRes.json();
    if (!createProductRes.ok) {
      throw new Error(createProductData.error || "Product save failed");
    }

    return createProductData;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const [vintedSelectedCategoryId, setVintedSelectedCategoryId] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps

  const [vintedSelectedCategoryName, setVintedSelectedCategoryName] =
    useState("");

  // ========== NEW: Product Settings from Backend ==========
  const [productSettings, setProductSettings] = useState({
    minImages: 0,
    fileSizeKb: 5120,
    sizeChartRequired: "not_required",
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [sizeChartFile, setSizeChartFile] = useState(null);
  const [sizeChartError, setSizeChartError] = useState("");

  const [vintedConnected, setVintedConnected] = useState(false);
  const [checkingVinted, setCheckingVinted] = useState(false);
  const [vintedLoadingCategories, setVintedLoadingCategories] = useState(false);

  const fetchVintedMainCategories = async () => {
    setVintedLoadingCategories(true);
    try {
      const res = await fetch(
        "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/categories/main",
      );
      const data = await res.json();
      if (data.success) {
        setVintedMainCategories(data.categories);
      } else {
        showError("Failed to load Vinted categories");
      }
    } catch (err) {
      console.error("Vinted categories fetch error:", err);
      showError("Could not connect to Vinted categories server");
    } finally {
      setVintedLoadingCategories(false);
    }
  };

  const fetchVintedSubCategories = async (parentCategoryId) => {
    setVintedLoadingCategories(true);
    try {
      const res = await fetch(
        `https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/categories/sub/${parentCategoryId}`,
      );
      const data = await res.json();
      if (data.success) {
        setVintedSubCategories(data.subcategories);
        return data.subcategories;
      } else {
        showError("Failed to load Vinted subcategories");
        return [];
      }
    } catch (err) {
      console.error("Vinted subcategories fetch error:", err);
      showError("Could not connect to Vinted categories server");
      return [];
    } finally {
      setVintedLoadingCategories(false);
    }
  };

  const fetchVintedCategoryPath = async (categoryId) => {
    try {
      const res = await fetch(
        `https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/categories/path/${categoryId}`,
      );
      const data = await res.json();
      if (data.success) {
        return data.path || [];
      } else {
        return [];
      }
    } catch (err) {
      console.error("Vinted category path error:", err);
      return [];
    }
  };

  const handleVintedCategoryClick = async (category) => {
    if (category.is_final_selection) {
      const path = await fetchVintedCategoryPath(category.category_id);
      const pathNames = path.map((cat) => cat.name);
      setFormData((prev) => ({
        ...prev,
        vintedCategory: pathNames.join(" > "),
        vintedCategoryId: category.category_id,
        vintedCategoryPath: pathNames,
      }));
      setVintedSelectedCategoryPath(pathNames);
      setVintedSelectedCategoryId(category.category_id);
      setVintedSelectedCategoryName(category.name);
      setVintedCurrentCategoryId(null);
      setVintedCategoryHistory([]);
    } else {
      setVintedCategoryHistory((prev) => [
        ...prev,
        {
          categoryId: vintedCurrentCategoryId,
          categoryName:
            vintedSelectedCategoryPath[vintedSelectedCategoryPath.length - 1] ||
            "",
        },
      ]);
      setVintedSelectedCategoryPath((prev) => [...prev, category.name]);
      setVintedCurrentCategoryId(category.category_id);
      const subs = await fetchVintedSubCategories(category.category_id);
      setVintedSubCategories(subs);
    }
  };

  const handleVintedBack = async () => {
    if (vintedCategoryHistory.length > 0) {
      const newPath = [...vintedSelectedCategoryPath];
      newPath.pop();
      setVintedSelectedCategoryPath(newPath);
      const prevCategory =
        vintedCategoryHistory[vintedCategoryHistory.length - 1];
      setVintedCategoryHistory((prev) => prev.slice(0, -1));
      setVintedCurrentCategoryId(prevCategory.categoryId);
      if (prevCategory.categoryId) {
        await fetchVintedSubCategories(prevCategory.categoryId);
      } else {
        setVintedCurrentCategoryId(null);
      }
    } else {
      setVintedCurrentCategoryId(null);
      setVintedSelectedCategoryPath([]);
    }
  };

  const resetVintedCategory = () => {
    setFormData((prev) => ({
      ...prev,
      vintedCategory: "",
      vintedCategoryId: "",
      vintedCategoryPath: [],
    }));
    setVintedSelectedCategoryPath([]);
    setVintedSelectedCategoryId("");
    setVintedSelectedCategoryName("");
    setVintedCurrentCategoryId(null);
    setVintedCategoryHistory([]);
    fetchVintedMainCategories();
  };

  const handleVintedIntegrate = async (e) => {
    e.stopPropagation();
    try {
      setCheckingVinted(true);

      // Pehle check karo extension connected hai ya nahi
      const res = await fetch(
        "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/health-vinted",
      );
      const data = await res.json();

      if (data.connected) {
        // ✅ Already connected - Directly Vinted new listing page kholo
        showSuccess("Opening Vinted listing page...");
        window.open("https://www.vinted.com/items/new", "_blank");
        setVintedConnected(true);
      } else {
        // ❌ Not connected - User ko guide karo
        showWarning(
          "Please open Vinted website in Chrome and refresh the page!",
        );
        window.open("https://www.vinted.com/items/new", "_blank");
        setVintedConnected(false);
      }
    } catch (err) {
      console.error("Vinted integration error:", err);
      showError("Failed to check Vinted extension status");
      setVintedConnected(false);
    } finally {
      setCheckingVinted(false);
    }
  };

  const buildVintedDescription = () => {
  const fields = [];

  if (formData.brand) fields.push(`Brand: ${formData.brand}`);
  // size: from vintedSize or first variant
  const size = formData.vintedSize || formData.variants?.[0]?.size;
  if (size) fields.push(`Size: ${size}`);
  if (formData.color) fields.push(`Color: ${formData.color}`);
  if (formData.material) fields.push(`Material: ${formData.material}`);

  // condition mapping (already defined inside uploadToVinted)
  const conditionMap = {
    new: "New with tags",
    "like-new": "Very good",
    excellent: "Very good",
    good: "Good",
    fair: "Satisfactory",
    poor: "Satisfactory",
  };
  const mappedCondition = conditionMap[formData.condition] || "Very good";
  fields.push(`Condition: ${mappedCondition}`);

  // shipping time – you can make it editable later, for now static
  fields.push(`Shipping time: 3-5 days`);

  return fields.join("\n");
};

  const uploadToVinted = async () => {
    setSubmissionStep("🚀 Starting Vinted automation...");
    const vintedApiTimer = startStepTimer("CROSS_LISTING_VINTED");
    let vintedApiStatus = "SUCCESS";
    const productFlowLogger = getProductFlowLogger();

    // ===== STEP 1: GET USER ID FROM LOCALSTORAGE =====
    const companyId = localStorage.getItem("company_id");
    const effectiveUserId = companyId || "default-user";

    console.log(
      "📤 Vinted Upload - User ID from localStorage:",
      effectiveUserId,
    );

    // ===== STEP 2: SYNC USER ID WITH BACKEND VIA API =====
    if (
      companyId &&
      companyId !== "default-user" &&
      companyId !== "null" &&
      companyId !== "undefined"
    ) {
      try {
        console.log("📤 Syncing userId with backend API:", companyId);

        const syncResponse = await fetch(
          "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/set-user-id",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              userId: companyId,
              source: "vinted-upload",
              timestamp: Date.now(),
            }),
          },
        );

        if (!syncResponse.ok) {
          console.warn("⚠️ Sync response not OK:", syncResponse.status);
        } else {
          const syncData = await syncResponse.json();
          if (productFlowLogger) {
            await productFlowLogger.logApiCall({
              company_id: companyId,
              service_name: "VINTED",
              external_service: "VINTED",
              endpoint: "/api/set-user-id",
              method: "POST",
              request: { userId: companyId },
              response: syncData,
              status_code: syncResponse.status,
              duration_ms: null,
              step: "CROSS_LISTING",
            });
          }
          console.log("✅ Backend sync response:", syncData);

          if (syncData.success) {
            console.log(
              `✅ UserId ${companyId} synced successfully! Broadcasted to ${syncData.clients || 0} clients`,
            );
          }
        }

        // Small delay for WebSocket to propagate
        console.log("⏳ Waiting 2 seconds for WebSocket to propagate...");
        await new Promise((resolve) => setTimeout(resolve, 2000));
      } catch (syncError) {
        console.error("❌ Failed to sync userId with backend:", syncError);
        await safeLogError("CROSS_LISTING_VINTED", syncError, {
          request_payload: { userId: companyId },
        });
        // Continue anyway - might still work with default ID
        console.log("⚠️ Continuing with userId:", effectiveUserId);
      }
    } else {
      console.log("⚠️ No valid companyId found, using:", effectiveUserId);
    }
    // ===== END USER ID SYNC =====

    try {
      // ===== STEP 3: CHECK EXTENSION HEALTH WITH USER ID =====
      setSubmissionStep("📡 Checking Vinted extension connection...");

      const healthUrl = `https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/health-vinted?userId=${effectiveUserId}`;
      console.log("🔍 Health check URL:", healthUrl);

      const healthCheck = await fetch(healthUrl);

      if (!healthCheck.ok) {
        console.warn("⚠️ Health check response not OK:", healthCheck.status);
        const errorText = await healthCheck.text();
        console.warn("Error response:", errorText);
        throw new Error(`Health check failed: ${healthCheck.status}`);
      }

      const healthData = await healthCheck.json();
      if (productFlowLogger) {
        await productFlowLogger.logApiCall({
          company_id: companyId,
          service_name: "VINTED",
          external_service: "VINTED",
          endpoint: "/api/health-vinted",
          method: "GET",
          request: { userId: effectiveUserId },
          response: healthData,
          status_code: healthCheck.status,
          duration_ms: null,
          step: "CROSS_LISTING",
        });
      }
      console.log("📊 Health check response:", healthData);

      if (!healthData.connected) {
        // Check if there are any active connections but with different user ID
        if (healthData.activeConnections && healthData.activeConnections > 0) {
          showError(
            `❌ Extension Connected But Wrong User ID!\n\n` +
              `Your User ID: ${effectiveUserId}\n` +
              `Active Connections: ${healthData.activeConnections}\n\n` +
              `The extension is connected but with a different user ID.\n` +
              `Please wait 5 seconds and try again.`,
            10000,
          );

          // Try to force sync again
          if (companyId) {
            console.log("🔄 Retrying userId sync...");
            await fetch(
              "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/set-user-id",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userId: companyId }),
              },
            );
          }
        } else {
          // No connections at all
          showError(
            "❌ Vinted extension not connected!\n\n" +
              "Current Status:\n" +
              `• User ID: ${effectiveUserId}\n` +
              `• Active Connections: ${healthData.activeConnections || 0}\n\n` +
              "Please follow these steps:\n" +
              "1. Open Vinted website in Chrome\n" +
              "2. Go to create listing page (https://www.vinted.com/items/new)\n" +
              "3. Refresh the page\n" +
              "4. Wait for 'Ready for Automation' notification (2-3 seconds)\n" +
              "5. Click 'Create Listing' again\n\n" +
              "The notification will appear in the top-right corner of Vinted tab.",
          );

          window.open("https://www.vinted.com/items/new", "_blank");
        }

        return {
          success: false,
          error: "Extension not connected",
          requiresUserAction: true,
          healthData: healthData,
        };
      }

      console.log(
        "✅ Extension connected successfully for user:",
        effectiveUserId,
      );
      // ===== END HEALTH CHECK =====

      // ===== STEP 4: PROCESS IMAGES =====
      setSubmissionStep("📸 Processing images...");
      const imagesToProcess = getImagesToSubmit();

      if (!imagesToProcess || imagesToProcess.length === 0) {
        throw new Error("No images to upload");
      }

      console.log(`🖼️ Processing ${imagesToProcess.length} images...`);

      const imagesBase64 = await Promise.all(
        imagesToProcess.map(async (url, index) => {
          try {
            console.log(
              `📸 Processing image ${index + 1}/${imagesToProcess.length}`,
            );
            const res = await fetch(url);
            const blob = await res.blob();
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
          } catch (imgError) {
            console.error(
              `❌ Error processing image ${index + 1}:`,
              url,
              imgError,
            );
            return null;
          }
        }),
      );

      const validImages = imagesBase64.filter((img) => img !== null);

      if (validImages.length === 0) {
        throw new Error("No valid images to upload after processing");
      }

      console.log(`✅ Processed ${validImages.length} images successfully`);
      // ===== END IMAGE PROCESSING =====

      // ===== STEP 5: PREPARE PAYLOAD =====
      setSubmissionStep("📦 Preparing Vinted data...");

      // Map condition to Vinted format
      const conditionMap = {
        new: "New with tags",
        "like-new": "Very good",
        excellent: "Very good",
        good: "Good",
        fair: "Satisfactory",
        poor: "Satisfactory",
      };

      // Map package size
      const packageSizeMap = {
        small: "small",
        medium: "medium",
        large: "large",
        extra_large: "large",
      };

      const formatVintedTitle = (str) => {
  if (!str) return "Product Listing";
  // Convert to "Title Case" (first letter of each word capital, rest lowercase)
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};
      const payload = {
        userId: effectiveUserId,
        title: formatVintedTitle(formData.title || "Product Listing"),
        description: buildVintedDescription(),
        price: parseFloat(formData.price || "1.00"),
        category: formData.vintedCategory || "",
        brand: formData.brand || "",
        condition: conditionMap[formData.condition] || "Very good",
        size: formData.vintedSize || "M / US 8-10",
        packageSize: packageSizeMap[formData.vintedPackageSize] || "medium",
        color: formData.color || "",
        images: validImages,
      };

      console.log("📤 Vinted Payload:", {
        ...payload,
        images: `${payload.images.length} images (base64 data hidden)`,
      });

      // Validate required fields
      if (!payload.title) throw new Error("Title is required");
      if (!payload.description) throw new Error("Description is required");
      if (!payload.price || isNaN(payload.price))
        throw new Error("Valid price is required");
      if (!payload.condition) throw new Error("Condition is required");
      // ===== END PAYLOAD PREP =====

      // ===== STEP 6: SEND TO VINTED API =====
      setSubmissionStep(
        "⏳ Uploading to Vinted (this may take up to 2 minutes)...",
      );

      const apiUrl = `https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/start-automation-vinted`;
      console.log("📡 API URL:", apiUrl);

      // Set timeout for fetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 150000); // 2.5 minutes timeout

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      console.log("📨 Response status:", res.status);

      if (!res.ok) {
        const errorText = await res.text();
        console.error("❌ Error response:", errorText);
        throw new Error(`Server responded with ${res.status}: ${errorText}`);
      }

      const data = await res.json();
      if (productFlowLogger) {
        await productFlowLogger.logApiCall({
          company_id: companyId,
          service_name: "VINTED",
          external_service: "VINTED",
          endpoint: "/api/start-automation-vinted",
          method: "POST",
          request: payload,
          response: data,
          status_code: res.status,
          duration_ms: null,
          step: "CROSS_LISTING",
        });
      }
      console.log("📩 Vinted Response:", data);
      // ===== END API CALL =====

      // ===== STEP 7: HANDLE RESPONSE =====
      if (data.success) {
        const productId = data.product?.id;
        const productUrl = data.product?.url;

        console.log(`🎉 VINTED SUCCESS! Product ID: ${productId}`);
        console.log(`🔗 Product URL: ${productUrl}`);

        // Show success message
        showSuccess(
          `✅ Vinted Upload Successful!\n\n` +
            `📦 Product ID: ${productId}\n` +
            `🔗 URL: ${productUrl}\n\n` +
            `📋 The item has been uploaded and saved to database.`,
          8000,
        );

        // Try to focus Vinted tab
        try {
          if (typeof chrome !== "undefined" && chrome.tabs) {
            chrome.tabs.query({ url: "*://*.vinted.com/*" }, (tabs) => {
              if (tabs && tabs.length > 0) {
                chrome.tabs.update(tabs[0].id, { active: true });
              }
            });
          }
        } catch (e) {
          console.log("Could not focus Vinted tab:", e);
        }

        return {
          success: true,
          data: data,
          productId: productId,
          productUrl: productUrl,
          message: "Vinted upload complete! Product saved to database.",
          requiresUserAction: false,
        };
      } else if (data.timeout) {
        // Timeout case
        console.warn("⚠️ Vinted timeout:", data.error);

        showWarning(
          "⚠️ Vinted Upload Timeout\n\n" +
            "The upload may have completed but we couldn't get the product ID.\n\n" +
            "Please check your Vinted account to verify.",
          10000,
        );

        return {
          success: false,
          error: data.error,
          timeout: true,
          requiresUserAction: false,
        };
      } else {
        // Failure case
        throw new Error(data.error || "Vinted upload failed");
      }
      // ===== END RESPONSE HANDLING =====
    } catch (err) {
      console.error("❌ Vinted Error:", err);
      vintedApiStatus = "FAILED";
      await safeLogError("CROSS_LISTING_VINTED", err);

      // Check for specific error types
      if (err.name === "AbortError") {
        showWarning(
          "⚠️ Upload timeout - Request took too long\n\n" +
            "Please check your Vinted account manually.",
        );

        return {
          success: false,
          error: "Request timeout",
          timeout: true,
          requiresUserAction: false,
        };
      }

      if (err.message.includes("Extension not connected")) {
        showError(
          "❌ Vinted extension not connected!\n\n" +
            "Please open Vinted and refresh the page.",
        );

        return {
          success: false,
          error: err.message,
          requiresUserAction: true,
        };
      } else if (
        err.message.includes("timeout") ||
        err.message.includes("TIMEOUT")
      ) {
        showWarning("⚠️ Upload timeout - Please check Vinted manually");

        return {
          success: false,
          error: err.message,
          timeout: true,
          requiresUserAction: false,
        };
      } else {
        showError(`❌ Vinted Error: ${err.message}`);

        return {
          success: false,
          error: err.message,
          requiresUserAction: false,
        };
      }
    } finally {
      await endStepTimer(vintedApiTimer, {
        step: "CROSS_LISTING",
        operation: "VINTED_API_FLOW",
        service_name: "VINTED",
        status: vintedApiStatus,
      });
    }
  };

  const handleFileUpload = async (event) => {
    const uploadFlowTimer = startStepTimer("UPLOAD");
    let uploadStatus = "SUCCESS";
    const files = Array.from(event.target.files);
    if (!files.length) return;
    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSize = productSettings.fileSizeKb * 1024;
    const validFiles = files.filter((file) => {
      if (!validTypes.includes(file.type)) {
        showWarning(`${file.name} is not a valid image.`);
        return false;
      }
      if (file.size > maxSize) {
        showWarning(`${file.name} exceeds ${productSettings.fileSizeKb} KB.`);
        return false;
      }
      return true;
    });
    if (!validFiles.length) return;

    const productFlowLogger = getProductFlowLogger();
    if (productFlowLogger) {
      await productFlowLogger.startUpload();
    }

    setFormData((prev) => ({
      ...prev,
      imageProcessing: true,
      title: "",
      description: "",
      brand: "",
      category: "",
      material: "",
      color: "",
      price: "",
      pricerange: "",
      condition: "",
      condition_score: "",
      condition_summary: "",
      authenticity_score: "",
      authenticity_summary: "",
      tags: "",
      categoryAspects: [],
      allCategoryAspects: [],
      categoryId: "",
    }));
    // try {
    //   const formDataObj = new FormData();
    //   validFiles.forEach((file) => formDataObj.append("images", file));
    //   const blurRes = await fetch(
    //     "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/blur/blur-detect",
    //     {
    //       method: "POST",
    //       body: formDataObj,
    //     },
    //   );
    //   // if (!blurRes.ok) throw new Error("Blur detection failed");
    //   const blurResults = await blurRes.json();
    //   if (!Array.isArray(blurResults)) {
    //     throw new Error("Invalid blur API response");
    //   }
    //   const acceptedImages = [];
    //   const blurStatuses = [];
    //   blurResults.forEach((item) => {
    //     const file = validFiles.find((f) => f.name === item.filename);
    //     if (!file) return;
    //     const isRejected = item.result.status === "rejected";
    //     blurStatuses.push(isRejected);
    //     acceptedImages.push(URL.createObjectURL(file));
    //   });
    //   setFormData((prev) => ({
    //     ...prev,
    //     uploadedImages: [...prev.uploadedImages, ...acceptedImages],
    //     blurStatuses,
    //   }));
    // } catch (err) {
    //   console.error("Blur detection error:", err);
    //   showError("Failed to process images");
    // } finally {
    //   setFormData((prev) => ({ ...prev, imageProcessing: false }));
    // }
    try {
      const hostedUrls = await uploadFilesToERP(validFiles);
      if (hostedUrls.length !== validFiles.length) {
        throw new Error("Some images failed to upload to storage");
      }

      const blurStatuses = validFiles.map(() => false);

      setFormData((prev) => ({
        ...prev,
        uploadedImages: [...prev.uploadedImages, ...hostedUrls],
        blurStatuses: [...prev.blurStatuses, ...blurStatuses],
      }));
    } catch (err) {
      console.error("Image upload error:", err);
      uploadStatus = "FAILED";
      await safeLogError("UPLOAD", err, {
        request_payload: { files_count: validFiles.length },
      });
      showError("Failed to upload images. Please try again.");
    } finally {
      await endStepTimer(uploadFlowTimer, {
        step: "UPLOAD",
        operation: "PROCESS_UPLOADED_IMAGES",
        service_name: "ADD_PRODUCT_PAGE",
        status: uploadStatus,
      });
      setFormData((prev) => ({ ...prev, imageProcessing: false }));
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const fakeEvent = { target: { files: files } };
      handleFileUpload(fakeEvent);
    }
  };

  const handleStartImageEditing = async () => {
    if (!formData.uploadedImages.length) return;

    setProcessing(true);
    const previousImages = [...processedImages]; // backup

    try {
      const processSingleImage = async (imageUrl, index) => {
        const res = await fetch(imageUrl);
        const blob = await res.blob();
        let file = new File([blob], `image-${index + 1}.png`, {
          type: blob.type,
        });

        return { file, blob };
      };

      // Load all images first
      const loadedImages = await Promise.all(
        formData.uploadedImages.map((img, i) => processSingleImage(img, i)),
      );

      const finalImages = [];

      // ================== 1️⃣ UPSCALE FIRST IMAGE ==================
      if (formData.upscaleImage && loadedImages[0]) {
        const upscaleTimer = startStepTimer("UPSCALE");
        let upscaleStatus = "SUCCESS";
        try {
          const productFlowLogger = getProductFlowLogger();
          if (productFlowLogger) {
            await productFlowLogger.logUpscale();
          }

          const form = new FormData();
          form.append("image", loadedImages[0].file);

          const response = await axios.post(
            "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/image/ghost-mannequin",
            form,
          );
          if (productFlowLogger) {
            await productFlowLogger.logApiCall({
              company_id: companyId,
              service_name: "IMAGE_UPSCALE",
              external_service: "INTERNAL_IMAGE",
              endpoint: "/api/image/ghost-mannequin",
              method: "POST",
              request: { image_count: 1 },
              response: response?.data,
              status_code: response?.status,
              duration_ms: null,
              step: "UPSCALE",
            });
          }

          if (response.data?.image) {
            const scaledBlob = await (await fetch(response.data.image)).blob();
            const scaledFile = new File([scaledBlob], `upscale-1.png`, {
              type: scaledBlob.type,
            });

            finalImages.push(scaledFile);
          }
        } catch (err) {
          console.warn("Upscale failed", err);
          upscaleStatus = "FAILED";
          await safeLogError("UPSCALE", err);
        } finally {
          await endStepTimer(upscaleTimer, {
            step: "UPSCALE",
            operation: "UPSCALE_IMAGE",
            service_name: "IMAGE_UPSCALE",
            status: upscaleStatus,
          });
        }
      }

      // ================== 2️⃣ BACKGROUND REMOVE ALL ==================
      const bgTasks = loadedImages.map(async ({ file }, index) => {
        if (!formData.backgroundRemoval) return file;

        const bgTimer =
          index === 0 ? startStepTimer("BACKGROUND_REMOVAL") : null;
        let bgStatus = "SUCCESS";
        try {
          if (index === 0) {
            const productFlowLogger = getProductFlowLogger();
            if (productFlowLogger) {
              await productFlowLogger.logBackgroundRemoval();
            }
          }

          const form = new FormData();
          form.append("image", file);

          const bgResponse = await axios.post(
            "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/image/remove-background",
            form,
          );
          if (index === 0) {
            const productFlowLogger = getProductFlowLogger();
            if (productFlowLogger) {
              await productFlowLogger.logApiCall({
                company_id: companyId,
                service_name: "BACKGROUND_REMOVAL",
                external_service: "INTERNAL_IMAGE",
                endpoint: "/api/image/remove-background",
                method: "POST",
                request: { image_count: loadedImages.length },
                response: bgResponse?.data,
                status_code: bgResponse?.status,
                duration_ms: null,
                step: "BACKGROUND_REMOVAL",
              });
            }
          }

          if (bgResponse.data?.success && bgResponse.data?.image) {
            const base64ToBlob = (base64) => {
              const arr = base64.split(",");
              const mime = arr[0].match(/:(.*?);/)[1];
              const bstr = atob(arr[1]);
              let n = bstr.length;
              const u8arr = new Uint8Array(n);
              while (n--) u8arr[n] = bstr.charCodeAt(n);
              return new Blob([u8arr], { type: mime });
            };

            const processedBlob = base64ToBlob(bgResponse.data.image);
            const bgFile = new File([processedBlob], `bg-${index + 1}.png`, {
              type: processedBlob.type,
            });

            return bgFile;
          }
        } catch (err) {
          console.warn("BG remove failed", err);
          bgStatus = "FAILED";
          await safeLogError("BACKGROUND_REMOVAL", err);
        } finally {
          if (index === 0) {
            await endStepTimer(bgTimer, {
              step: "BACKGROUND_REMOVAL",
              operation: "REMOVE_BACKGROUND",
              service_name: "BACKGROUND_REMOVAL",
              status: bgStatus,
            });
          }
        }

        return file;
      });

      const bgImages = await Promise.all(bgTasks);

      // Add BG images after upscale
      finalImages.push(...bgImages);

      const hostedProcessed = await ensureHostedImageUrls(finalImages);
      if (!hostedProcessed.length) {
        throw new Error("Processed images could not be uploaded to storage");
      }

      setProcessedImages(hostedProcessed);
      setEditedImages([]);
      showSuccess("Image processing completed!");
    } catch (err) {
      console.error("Image processing error:", err);
      showError("Image processing failed");
    } finally {
      setProcessing(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.startsWith("metafields.")) {
      const key = field.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        metafields: {
          ...prev.metafields,
          [key]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleVariantChange = (variantId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      variants: prev.variants.map((variant) =>
        variant.id === variantId ? { ...variant, [field]: value } : variant,
      ),
    }));
  };

  const handleSizeChartUpload = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSizeChartFile(null);
      setSizeChartError("");
      return;
    }
    const maxSize = productSettings.fileSizeKb * 1024;
    if (file.size > maxSize) {
      setSizeChartError(`File size exceeds ${productSettings.fileSizeKb} KB.`);
      setSizeChartFile(null);
      return;
    }
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "application/pdf",
    ];
    if (!allowedTypes.includes(file.type)) {
      setSizeChartError("Only JPG, PNG, WEBP or PDF allowed.");
      setSizeChartFile(null);
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    setSizeChartFile({ file, previewUrl });
    setSizeChartError("");
  };

  const addVariant = () => {
    const newId = Math.max(...formData.variants.map((v) => v.id), 0) + 1;
    setFormData((prev) => ({
      ...prev,
      variants: [...prev.variants, { id: newId, size: "", color: "", sku: "" }],
    }));
  };

  const removeVariant = (variantId) => {
    if (formData.variants.length > 1) {
      setFormData((prev) => ({
        ...prev,
        variants: prev.variants.filter((variant) => variant.id !== variantId),
      }));
    }
  };
  const getEbayPolicies = async () => {
    const company_id = localStorage.getItem("company_id");
    console.log("company_id", company_id);
    if (!company_id) return; // ❌ no company id → exit

    try {
      // 1️⃣ get ebay policies
      // const res = await fetch(
      //   `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getEbayPolicies/${company_id}`,
      // );
      // const policies = await res.json();

      // 2️⃣ get saved ebay policies
      const savedRes = await fetch(
        `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getSavedEbayPolicies/${company_id}`,
      );
      const savedPolicies = await savedRes.json();

      setSavedEbayPolicies(savedPolicies);
      // 3️⃣ just store responses (no setSelected)
      //setEbayPolicies(policies);
      setSavedEbayPolicies(savedPolicies);
    } catch (error) {
      console.error("eBay policy fetch failed:", error);
    }
  };

  const handleMarketplaceToggle = (marketplaceId) => {
    setFormData((prev) => {
      const updatedMarketplaces = prev.selectedMarketplaces.includes(
        marketplaceId,
      )
        ? prev.selectedMarketplaces.filter((id) => id !== marketplaceId)
        : [...prev.selectedMarketplaces, marketplaceId];

      console.log("selected marketplaces", updatedMarketplaces);

      return {
        ...prev,
        selectedMarketplaces: updatedMarketplaces,
      };
    });

    if (marketplaceId === "vinted") {
      fetchVintedMainCategories();
    }

    if (marketplaceId === "ebay") {
      getEbayPolicies();
    }
  };

  // Check SmartScan/demo limits for company 13 before proceeding
  const checkUsageLimit = async () => {
    try {
      const company_id = localStorage.getItem("company_id");
      // Only enforce limit for demo company 13
      if (company_id !== "13") return true;

      const res = await fetch(
        `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/smartscan//counts?company_id=${company_id}`,
      );
      const data = await res.json();

      if (!res.ok || !data?.success) {
        console.warn(
          "Smartscan counts API failed or returned invalid data:",
          data,
        );
        return true; // Don't block flow on API failure
      }

      const smartscanCount = Number(data.data?.smartscan || 0);
      const productsCount = Number(data.data?.products || 0);

      if (smartscanCount >= 100 || productsCount >= 100) {
        showWarning(
          "Your SmartScan demo limit has been reached. You have created 100 items. Please contact ThriftOps support to upgrade your plan or extend your usage limit.",
        );
        return false;
      }

      return true;
    } catch (err) {
      console.error("Error checking SmartScan usage limits:", err);
      // Fail-open: allow user to proceed if limit check fails
      return true;
    }
  };

  const nextStep = async () => {
    if (currentStep < steps.length) {
      // When moving from step 1 (image upload), enforce demo limit for company 13
      if (currentStep === 1) {
        console.log("🔍 productSettings.minImages:", productSettings.minImages);
        console.log(
          "🔍 uploadedImages length:",
          formData.uploadedImages.length,
        );
        const canProceed = await checkUsageLimit();
        if (!canProceed) return;
        // Enforce minimum images
        const minReq = productSettings.minImages;
        if (minReq > 0 && formData.uploadedImages.length < minReq) {
          showWarning(`Please upload at least ${minReq} product image(s).`);
          return;
        }
      }

      if (currentStep === 2 && !isProductConditionAcceptable()) {
        showWarning("Product condition is not acceptable");
        return;
      }

      setCurrentStep((prev) => prev + 1);
      if (currentStep === 6) {
        runImageProcessing();
      }
    }
  };

  const runImageProcessing = async () => {
    setProcessingImages(true);
    setProgress(0);
    const finalImages = [];
    for (let i = 0; i < formData.uploadedImages.length; i++) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      try {
        let res = await fetch(formData.uploadedImages[i]);
        let blob = await res.blob();
        let file = new File([blob], `image-${i + 1}.png`, { type: blob.type });
        if (formData.upscaleImage) {
          const form = new FormData();
          form.append("images", file);
          const response = await axios.post(
            "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/scaleup/scale-up",
            form,
          );
          const scaledUrl = response.data?.[0]?.result?.url;
          const scaledBlob = await (await fetch(scaledUrl)).blob();
          file = new File([scaledBlob], `scaled-${i + 1}.png`, {
            type: scaledBlob.type,
          });
        }
        if (formData.backgroundRemoval) {
          const bgBlob = await removeBackground(file);
          file = new File([bgBlob], `bg-${i + 1}.png`, { type: bgBlob.type });
        }
        finalImages.push(URL.createObjectURL(file));
        setProcessedImages([...finalImages]);
        setProgress(
          Math.round(((i + 1) / formData.uploadedImages.length) * 100),
        );
      } catch (err) {
        console.error("Image processing failed for image", i, err);
      }
    }
    setProcessingImages(false);
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const goToStep = (stepId) => {
    if (stepId <= getMaxAccessibleStep()) {
      setCurrentStep(stepId);
    }
  };

  const getMaxAccessibleStep = () => {
    let maxStep = 1;
    if (formData.uploadedImages.length > 0) maxStep = 2;
    const hasListingDetails =
      formData.uploadedImages.length > 0 &&
      formData.title &&
      formData.description &&
      formData.sku &&
      formData.price &&
      formData.quantity &&
      isProductConditionAcceptable();

    if (hasListingDetails) {
      maxStep = 3;
    }
    if (hasListingDetails && formData.selectedMarketplaces.length > 0) {
      maxStep = 4;
    }
    if (hasListingDetails && formData.selectedMarketplaces.length > 0) {
      maxStep = 5;
    }
    return maxStep;
  };

  const isStepClickable = (stepId) => {
    return stepId <= getMaxAccessibleStep();
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.uploadedImages.length > 0;
      case 2:
        return (
          formData.title &&
          formData.description &&
          formData.sku &&
          formData.price &&
          formData.quantity &&
          isProductConditionAcceptable()
        );
      case 3:
        return formData.selectedMarketplaces.length > 0;
      case 4:
        return true;
      case 5:
        return true;
      default:
        return false;
    }
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      uploadedImages: prev.uploadedImages.filter((_, i) => i !== index),
      blurStatuses: prev.blurStatuses.filter((_, i) => i !== index),
    }));
  };

  // // Right Rotate (Clockwise)
  // const rotateImage = async (index) => {
  //   await rotateImageByDegrees(index, 90);
  // };

  // // Left Rotate (Counter-Clockwise)
  // const rotateImageLeft = async (index) => {
  //   await rotateImageByDegrees(index, -90);
  // };

  const rotateImage = async (index) => {
    const imageUrl = formData.uploadedImages[index];
    await rotateImageByDegrees(index, 90, imageUrl);
  };

  const rotateImageLeft = async (index) => {
    const imageUrl = formData.uploadedImages[index];
    await rotateImageByDegrees(index, -90, imageUrl);
  };

  const rotateImageByDegrees = async (index, degrees, imageUrl) => {
    // ✅ Directly access formData - no setFormData trick
    // const imageUrl = formData.uploadedImages[index];

    if (!imageUrl) {
      console.error(`No image found at index ${index}`);
      showError("Image not found");
      return;
    }

    console.log(`Rotating image ${index + 1}, URL:`, imageUrl.substring(0, 80));

    try {
      // Fetch the image
      const response = await fetch(imageUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();

      if (!blob || blob.size === 0) {
        throw new Error("Empty image data");
      }

      const blobUrl = URL.createObjectURL(blob);

      const img = new Image();

      const loadedImage = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("Image load timeout"));
        }, 10000);

        img.onload = () => {
          clearTimeout(timeout);
          resolve(img);
        };

        img.onerror = () => {
          clearTimeout(timeout);
          reject(new Error("Image load failed"));
        };

        img.src = blobUrl;
      });

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const radians = (degrees * Math.PI) / 180;
      const isVertical = Math.abs(degrees % 180) === 90;

      if (isVertical) {
        canvas.width = loadedImage.height;
        canvas.height = loadedImage.width;
      } else {
        canvas.width = loadedImage.width;
        canvas.height = loadedImage.height;
      }

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(radians);
      ctx.drawImage(
        loadedImage,
        -loadedImage.width / 2,
        -loadedImage.height / 2,
      );

      const rotatedBlob = await new Promise((resolve) => {
        canvas.toBlob(resolve, "image/png");
      });
      if (!rotatedBlob) {
        // <-- ye line ADD karo
        showError("Image rotation failed (canvas error)");
        return;
      }

      const rotatedFile = new File([rotatedBlob], `rotated-${index + 1}.png`, {
        type: "image/png",
      });

      // ADD KAR YE:
      console.log(
        "File size:",
        rotatedFile.size,
        "File name:",
        rotatedFile.name,
        "File type:",
        rotatedFile.type,
      );

      // uploadFilesToERP call ke baad ye replace karo:
      // const [rotatedHostedUrl] = await uploadFilesToERP([rotatedFile]);
      // Retry logic with URL validation
      const uploadWithRetry = async (
        file,
        expectedCount = 1,
        maxRetries = 3,
      ) => {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          // Random delay - race condition avoid karo
          await new Promise((r) =>
            setTimeout(r, attempt * 300 + Math.random() * 200),
          );

          try {
            const urls = await uploadFilesToERP([file]);

            // Validate - sirf 1 URL chahiye, 5 nahi
            if (urls && urls.length === expectedCount) {
              console.log(`✅ Upload attempt ${attempt} successful`);
              return urls;
            }

            console.warn(
              `⚠️ Attempt ${attempt}: Got ${urls?.length} URLs, expected ${expectedCount}. Retrying...`,
            );
          } catch (err) {
            console.warn(`⚠️ Attempt ${attempt} failed:`, err.message);
          }
        }
        return [];
      };

      const urls = await uploadWithRetry(rotatedFile, 1);
      const rotatedHostedUrl = urls[0];

      if (!rotatedHostedUrl || !isHostedImageUrl(rotatedHostedUrl)) {
        throw new Error("Failed to upload rotated image after retries");
      }
      if (!rotatedHostedUrl || !isHostedImageUrl(rotatedHostedUrl)) {
        throw new Error("Failed to upload rotated image");
      }

      // Update state
      setFormData((prev) => {
        const newUploadedImages = [...prev.uploadedImages];
        newUploadedImages[index] = rotatedHostedUrl;

        return {
          ...prev,
          uploadedImages: newUploadedImages,
        };
      });

      // Update processedImages if exists
      // Update state — jo display ho rahi hai wahi update karo
      if (processedImages.length > 0) {
        setProcessedImages((prev) => {
          const newImages = [...prev];
          newImages[index] = rotatedHostedUrl;
          return newImages;
        });
      } else {
        setEditedImages((prev) => {
          const newImages = [...prev];
          newImages[index] = rotatedHostedUrl;
          return newImages;
        });
      }

      URL.revokeObjectURL(blobUrl);

      console.log(`✅ Image ${index + 1} rotated successfully`);
    } catch (err) {
      console.error(`Rotation failed for image ${index + 1}:`, err);
      showError(`Failed to rotate image ${index + 1}`);
    }
  };

  // Rotate processed images (not original)
  const rotateProcessedImage = async (index, degrees) => {
    // ✅ processedImages ya editedImages dono check karo
    const currentImages =
      processedImages.length > 0 ? processedImages : editedImages;

    if (!currentImages.length || !currentImages[index]) {
      showError("No image found to rotate");
      return;
    }

    const imageUrl = currentImages[index];

    try {
      console.log(`Rotating processed image ${index + 1}`);

      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error("Failed to fetch");

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const img = new Image();

      const loadedImage = await new Promise((resolve, reject) => {
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = blobUrl;
      });

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const radians = (degrees * Math.PI) / 180;
      const isVertical = Math.abs(degrees % 180) === 90;

      if (isVertical) {
        canvas.width = loadedImage.height;
        canvas.height = loadedImage.width;
      } else {
        canvas.width = loadedImage.width;
        canvas.height = loadedImage.height;
      }

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(radians);
      ctx.drawImage(
        loadedImage,
        -loadedImage.width / 2,
        -loadedImage.height / 2,
      );

      const rotatedBlob = await new Promise((resolve) => {
        canvas.toBlob(resolve, "image/png");
      });

      const rotatedFile = new File(
        [rotatedBlob],
        `processed-rotated-${index + 1}.png`,
        { type: "image/png" },
      );
      // const [rotatedHostedUrl] = await uploadFilesToERP([rotatedFile]);
      // if (!rotatedHostedUrl || !isHostedImageUrl(rotatedHostedUrl)) {
      //   throw new Error("Failed to upload rotated image");
      // }
      // ✅ FIX — jo display ho rahi hai wahi update karo
      const urls = await uploadFilesToERP([rotatedFile]);
      const rotatedHostedUrl = urls[0];

      if (!rotatedHostedUrl || !isHostedImageUrl(rotatedHostedUrl)) {
        throw new Error("Failed to upload rotated image");
      }

      if (processedImages.length > 0) {
        setProcessedImages((prev) => {
          const newImages = [...prev];
          newImages[index] = rotatedHostedUrl;
          return newImages;
        });
      } else {
        setEditedImages((prev) => {
          const newImages = [...prev];
          newImages[index] = rotatedHostedUrl;
          return newImages;
        });
      }

      // setProcessedImages((prev) => {
      //   const newImages = [...prev];
      //   newImages[index] = rotatedHostedUrl;
      //   return newImages;
      // });

      URL.revokeObjectURL(blobUrl);
      console.log(`✅ Processed image ${index + 1} rotated`);
    } catch (err) {
      console.error("Rotation failed:", err);
      showError("Failed to rotate image");
    }
  };

  // Wrapper functions for processed images
  const rotateProcessedImageRight = (index) => {
    rotateProcessedImage(index, 90);
  };

  const rotateProcessedImageLeft = (index) => {
    rotateProcessedImage(index, -90);
  };

  const [loadingAspects, setLoadingAspects] = useState(false);

  const handleCategoryAspects = useCallback(
    async (categoryTerm) => {
      const ebayAutofillTimer = startStepTimer("EBAY_AUTOFILL");
      try {
        setLoadingAspects(true);
        const companyId = localStorage.getItem("company_id");
        const catResponse = await fetch(
          `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getCategoriesFBThriftops/${companyId}?category_term=${categoryTerm}`,
          { method: "POST", headers: { "Content-Type": "application/json" } },
        );
        const catData = await catResponse.json();
        const productFlowLogger = getProductFlowLogger();
        if (productFlowLogger) {
          await productFlowLogger.logApiCall({
            company_id: companyId,
            service_name: "EBAY_CATEGORY_SUGGESTION",
            external_service: "EBAY",
            endpoint: `/getCategoriesFBThriftops/${companyId}`,
            method: "POST",
            request: { category_term: categoryTerm },
            response: catData,
            status_code: catResponse.status,
            duration_ms: null,
            step: "EBAY_AUTOFILL",
          });
        }
        if (!catData.suggestedCategories?.length) {
          setLoadingAspects(false);
          await endStepTimer(ebayAutofillTimer, {
            step: "EBAY_AUTOFILL",
            operation: "FETCH_EBAY_CATEGORY_ASPECTS",
            service_name: "EBAY_AUTOFILL",
            status: "FAILED",
          });
          return;
        }
        const firstCategory = catData.suggestedCategories[0].category;
        const aspectsResponse = await fetch(
          `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getCategoryAspectsRAPIDAPI/${companyId}?category_id=${firstCategory.categoryId}`,
          { method: "POST", headers: { "Content-Type": "application/json" } },
        );
        const aspectsData = await aspectsResponse.json();
        if (productFlowLogger) {
          await productFlowLogger.logApiCall({
            company_id: companyId,
            service_name: "EBAY_CATEGORY_ASPECTS",
            external_service: "EBAY",
            endpoint: `/getCategoryAspectsRAPIDAPI/${companyId}`,
            method: "POST",
            request: { category_id: firstCategory.categoryId },
            response: aspectsData,
            status_code: aspectsResponse.status,
            duration_ms: null,
            step: "EBAY_AUTOFILL",
          });
          await productFlowLogger.logAiCall({
            ai_service: "EBAY_CATEGORY_AUTOFILL",
            input_quality: formData.uploadedImages?.length ? "GOOD" : "LOW",
            success: Boolean(aspectsData?.category_aspects?.aspects?.length),
            confidence_score: catData.suggestedCategories?.[0]?.score || null,
            input: { category_term: categoryTerm },
            output: {
              suggested_category: firstCategory,
              aspects_count:
                aspectsData?.category_aspects?.aspects?.length || 0,
            },
            step: "EBAY_AUTOFILL",
            model_version: "N/A",
          });
        }
        setFormData((prev) => {
          const mappedAspects =
            aspectsData.category_aspects?.aspects
              .filter(
                (aspect) =>
                  (aspect.aspectConstraint?.aspectUsage || "").toUpperCase() ===
                  "RECOMMENDED",
              )
              .map((aspect) => {
                const key = aspect.localizedAspectName
                  .replace(/\s+/g, "_")
                  .toLowerCase();
                return {
                  ...aspect,
                  key,
                  value: prev[key] || "",
                };
              }) || [];
          const newValues = {};
          mappedAspects.forEach((a) => {
            if (a.value) newValues[a.key] = a.value;
          });
          const allAspects =
            aspectsData.category_aspects?.aspects.map((aspect) => {
              const key = aspect.localizedAspectName
                .replace(/\s+/g, "_")
                .toLowerCase();
              return {
                ...aspect,
                key,
                value: prev[key] || "",
              };
            }) || [];
          return {
            ...prev,
            ...newValues,
            categoryAspects: mappedAspects,
            categoryId: firstCategory.categoryId,
            allCategoryAspects: allAspects,
            // Add suggested categories here
            allsuggestedCategories: catData.suggestedCategories || [],
          };
        });
        setLoadingAspects(false);
        await endStepTimer(ebayAutofillTimer, {
          step: "EBAY_AUTOFILL",
          operation: "FETCH_EBAY_CATEGORY_ASPECTS",
          service_name: "EBAY_AUTOFILL",
          status: "SUCCESS",
        });
      } catch (err) {
        console.error("Error fetching aspects:", err);
        await safeLogError("EBAY_AUTOFILL", err, {
          request_payload: { category_term: categoryTerm },
        });
        setLoadingAspects(false);
        await endStepTimer(ebayAutofillTimer, {
          step: "EBAY_AUTOFILL",
          operation: "FETCH_EBAY_CATEGORY_ASPECTS",
          service_name: "EBAY_AUTOFILL",
          status: "FAILED",
        });
      }
    },
    [formData.uploadedImages],
  );

  const [mappedSent, setMappedSent] = useState(false);

  useEffect(() => {
    if (
      !mappedSent &&
      autoFillResponse &&
      Array.isArray(formData.categoryAspects) &&
      formData.categoryAspects.length > 0
    ) {
      sendMappedCategories({
        autofillResponse: autoFillResponse,
        mappedAspects: formData.categoryAspects,
      });
      setMappedSent(true);
    }
  }, [autoFillResponse, formData.categoryAspects, mappedSent]);

  const sendMappedCategories = async ({ autofillResponse, mappedAspects }) => {
    try {
      const res = await fetch(
        "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getMappedCategories",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            autofillResponse,
            mappedAspects,
          }),
        },
      );
      const data = await res.json();
      console.log("Backend combined response:", data);
      console.log("Filled aspects from Gemini:", data.geminiFilledAspects);
      const filled = data.geminiFilledAspects || [];
      setFormData((prev) => {
        const updatedAspects = prev.categoryAspects.map((aspect) => {
          const filledAspect = filled.find((f) => f.key === aspect.key);
          return {
            ...aspect,
            value: filledAspect?.value || "N/A",
          };
        });

        // Gemini ne size fill ki ho toh title update karo
        const sizeVal = getSizeFromAspects(updatedAspects);
        const baseTitle = prev._baseTitleForSize || prev.title;
        const newTitle = sizeVal ? `${baseTitle} ${sizeVal}` : baseTitle;

        return {
          ...prev,
          categoryAspects: updatedAspects,
          title: newTitle,
        };
      });
    } catch (err) {
      console.error("Failed to send combined data:", err);
    }
  };

  useEffect(() => {
    if (typeof formData.category === "string" && formData.category.trim()) {
      handleCategoryAspects(formData.category);
    }
  }, [formData.category, handleCategoryAspects]);

  // const handleAspectChange = (key, val) => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     categoryAspects: prev.categoryAspects.map((a) =>
  //       a.key === key ? { ...a, value: val } : a,
  //     ),
  //     [key]: val,
  //   }));
  // };

  // handleAspectChange function ke upar add karo
  const getSizeFromAspects = (aspects) => {
    if (!Array.isArray(aspects)) return null;
    const sizeAspect = aspects.find((a) =>
      a.key?.toLowerCase().includes("size"),
    );
    const val = sizeAspect?.value;
    if (!val || val === "N/A" || val.trim() === "") return null;
    return val.trim();
  };

  const handleAspectChange = (key, val) => {
    setFormData((prev) => {
      const updatedAllAspects = prev.allCategoryAspects.map((a) =>
        a.key === key ? { ...a, value: val } : a,
      );

      const updatedCategoryAspects = prev.categoryAspects.map((a) =>
        a.key === key ? { ...a, value: val } : a,
      );

      // Size aspect change hone par title update karo
      let updatedTitle = prev.title;
      if (key.toLowerCase().includes("size")) {
        const baseTitle = prev._baseTitleForSize || prev.title;
        if (val && val !== "N/A" && val.trim() !== "") {
          updatedTitle = `${baseTitle} - Size ${val.trim()}`;
        } else {
          updatedTitle = baseTitle;
        }
      }

      return {
        ...prev,
        allCategoryAspects: updatedAllAspects,
        categoryAspects: updatedCategoryAspects,
        [key]: val,
        title: updatedTitle,
      };
    });
  };

  const handleAutoFill = async () => {
    if (!formData.uploadedImages.length) {
      showWarning("Please upload at least one image first!");
      return;
    }
    try {
      const productFlowLogger = getProductFlowLogger();
      if (productFlowLogger) {
        await productFlowLogger.logAutofill();
      }

      setIsAutoFilling(true);
      setAutoFillMessageIndex(0);
      const form = new FormData();
      for (let i = 0; i < formData.uploadedImages.length; i++) {
        const res = await fetch(formData.uploadedImages[i]);
        const blob = await res.blob();
        form.append("images", blob, `product-${i + 1}.png`);
      }
      await sendAutoFillRequest(form);
    } catch (err) {
      console.error("AutoFill Error", err);
      await safeLogError("AUTOFILL", err, {
        request_payload: { images_count: formData.uploadedImages.length },
      });
      showError("Auto-fill failed");
    } finally {
      setIsAutoFilling(false);
    }
  };

  const sendAutoFillRequest = async (form) => {
    const autofillTimer = startStepTimer("AUTOFILL");
    let autofillStatus = "SUCCESS";
    try {
      const productFlowLogger = getProductFlowLogger();
      form.append("companyId", companyId);
      // Just extract keys from activeMetafields
      if (activeMetafields && activeMetafields.length > 0) {
        console.log("Sending full metafields objects:", activeMetafields);
        form.append("metafields", JSON.stringify(activeMetafields));
      }
      // Now send the request
      const response = await axios.post(
        "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/autofill/analyze",
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (productFlowLogger) {
        await productFlowLogger.logApiCall({
          company_id: companyId,
          service_name: "AUTOFILL_API",
          external_service: "AUTOFILL",
          endpoint: "/api/autofill/analyze",
          method: "POST",
          request: { images_count: formData.uploadedImages.length },
          response: response?.data,
          status_code: response?.status,
          duration_ms: null,
          step: "AUTOFILL",
        });
      }

      console.log("Analyze response:", response.data);
      setAutoFillResponse(response.data);

      // Map metafields
      const data = response.data;

      // 🔥 yeh important line hai
      const mappedMetafields = buildMetafieldsFromAnalysis(
        activeMetafields,
        data,
      );

      console.log("mappedMetafields", mappedMetafields);
      // You can still update formData as before
      const analysis = response.data.analysis || {};
      const product_metadata = response.data.product_metadata || {};
      const price = response.data.price || {};
      const authenticity = response.data.authenticity || {};
      if (productFlowLogger) {
        await productFlowLogger.logAiCall({
          ai_service: "PRODUCT_AUTOFILL",
          input_quality: formData.uploadedImages?.length ? "GOOD" : "LOW",
          success: true,
          confidence_score: authenticity.score || null,
          input: { images_count: formData.uploadedImages.length },
          output: {
            title: response.data?.analysis?.seo_title || "",
            recommended_price: response.data?.price?.recommended || null,
            category: response.data?.analysis?.category || null,
          },
          step: "AUTOFILL",
          model_version: response.data?.model_version || "N/A",
        });
      }

      const conditionScore = Number(analysis.condition || 0);
      let condition = "POOR";
      if (conditionScore >= 10) condition = "NEW";
      else if (conditionScore >= 9) condition = "LIKE-NEW";
      else if (conditionScore >= 8) condition = "USED_EXCELLENT";
      else if (conditionScore >= 7) condition = "USED_GOOD";
      else if (conditionScore >= 6) condition = "USED_FAIR";

      const hierarchy = analysis.category || product_metadata.category || {};
      const categoryString = [
        hierarchy.l1_gender,
        hierarchy.l2_type,
        hierarchy.l3_category,
        hierarchy.l4_subcategory,
      ]
        .filter(Boolean)
        .join(" > ");

      setFormData((prev) => ({
        ...prev,
        metafields: mappedMetafields,
        title: analysis.seo_title || product_metadata.title || prev.title,
        _baseTitleForSize:
          analysis.seo_title || product_metadata.title || prev.title,
        description:
          analysis.seo_description ||
          product_metadata.description ||
          prev.description,
        sku: product_metadata.sku || prev.sku,
        brand: analysis.brand || "N/A",
        category: categoryString || prev.category,
        material: analysis.material || prev.material,
        color: analysis.color || prev.color,
        price: price.recommended || prev.price,
        pricerange:
          price.low && price.high
            ? `${price.low} - ${price.high}`
            : prev.pricerange,
        condition,
        condition_score: conditionScore,
        condition_summary:
          analysis.condition_summary ||
          analysis.condition?.summary ||
          prev.condition_summary,
        authenticity_score: authenticity.score || prev.authenticity_score,
        authenticity_summary: authenticity.summary || prev.authenticity_summary,
        tags: analysis.seo_tags
          ? analysis.seo_tags.split(",").map((t) => t.trim())
          : product_metadata.seo_tags
            ? product_metadata.seo_tags.split(",").map((t) => t.trim())
            : prev.tags,
      }));
    } catch (err) {
      console.error("API Error:", err);
      autofillStatus = "FAILED";
      await safeLogError("AUTOFILL", err, {
        request_payload: {
          images_count: formData.uploadedImages.length,
          metafields_count: activeMetafields?.length || 0,
        },
        response_payload: err?.response?.data || null,
      });
      showError("Auto-fill failed");
    } finally {
      await endStepTimer(autofillTimer, {
        step: "AUTOFILL",
        operation: "AUTOFILL_ANALYZE",
        service_name: "AUTOFILL_API",
        status: autofillStatus,
      });
    }
  };

  const handleSaveDraft = async () => {
    try {
      setIsSubmitting(true);
      setSubmissionStep("💾 Saving product to database...");
      setSubmissionSuccess(false);

      const companyId = localStorage.getItem("company_id");
      const createProductData = await saveProductToDatabase("submitted");
      // ========== EBAY INSERT (if ebay selected) ==========
      if (formData.selectedMarketplaces.includes("ebay")) {
        try {
          const ebayInsertData = {
            product_id: createProductData.product_id,
            ebay_item_id: null,
            sku: formData.sku || `SKU-${Date.now()}`,
            title: formData.title || "Draft Product",
            description: formData.description || "",
            aspects: JSON.stringify(
              buildEbayAspects(formData.categoryAspects || []),
            ),
            condition: (formData.condition || "NEW").toUpperCase(),
            conditionDescription: formData.condition_summary || null,
            quantity: parseInt(formData.quantity) || 1,
            category_id: formData.categoryId || "177837",
            price: parseFloat(formData.price) || 0,
            currency: "GBP",
            days: 0,
            ebay_store_id: parseInt(companyId),
          };

          await fetch(
            "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/product/ebay-product-insert",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(ebayInsertData),
            },
          );
        } catch (ebayErr) {
          console.error("eBay draft insert failed:", ebayErr);
          // Draft save fail nahi hoga eBay insert fail hone se
        }
      }
      setSubmissionSuccess(true);
      showSuccess("✅ Product saved successfully!");

      setTimeout(() => {
        localStorage.removeItem("appState_addProduct");
        window.location.reload();
      }, 2000);
    } catch (err) {
      console.error("Save product error:", err);
      showError(`❌ Product save failed: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    let crossListingTimer = null;
    try {
      console.log(formData.categoryAspects);
      console.log(formData.allCategoryAspects);
      const productFlowLogger = getProductFlowLogger();

      setIsSubmitting(true);
      setSubmissionSuccess(false);
      setSubmissionStep("📦 Preparing product data...");

      const selectedMps = formData.selectedMarketplaces || [];
      if (selectedMps.length === 0) {
        showWarning("⚠️ Please select at least one marketplace!");
        setIsSubmitting(false);
        return;
      }

      // Size chart validation (if required)
      if (productSettings.sizeChartRequired === "required" && !sizeChartFile) {
        showWarning(
          "Size chart is required. Please upload a size chart before submitting.",
        );
        setIsSubmitting(false);
        return;
      }
      crossListingTimer = startStepTimer("CROSS_LISTING");

      if (selectedMps.includes("ebay") && productFlowLogger) {
        await productFlowLogger.logEbayAutofill();
      }

      const companyId = localStorage.getItem("company_id");
      const results = [];
      const errors = [];
      const pendingActions = []; // For marketplaces that need user action

      // Create an array of marketplace promises
      const marketplacePromises = [];

      // ========== SHOPIFY ==========
      if (selectedMps.includes("shopify")) {
        const shopifyPromise = (async () => {
          const shopifyTimer = startStepTimer("CROSS_LISTING_SHOPIFY");
          let shopifyStatus = "SUCCESS";
          try {
            setSubmissionStep("🛒 Processing Shopify...");

            // 🔥 FUNCTION 1: Check if image is base64
            const isBase64 = (str) => {
              if (typeof str !== "string") return false;
              return (
                str.startsWith("data:image") ||
                str.startsWith("iVBORw0KGgo") || // PNG base64 start
                str.startsWith("/9j/4AAQSkZ")
              ); // JPEG base64 start
            };

            // 🔥 FUNCTION 2: Convert base64 to hosted URL
            const convertBase64ToHostedUrl = async (url) => {
              // Agar already hosted URL hai toh wapas kar do
              if (url.startsWith("http") && !url.startsWith("blob:")) {
                console.log(
                  `✅ Already hosted URL: ${url.substring(0, 50)}...`,
                );
                return url;
              }

              // Blob URL ya base64 ko ERP pe upload karo
              console.log(`📸 Converting to hosted URL via ERP...`);

              let blob;

              // Check if it's base64 or blob URL
              if (
                url.startsWith("data:image") ||
                (typeof url === "string" && url.includes("base64"))
              ) {
                // Base64 to blob conversion
                let base64String = url;
                if (base64String.includes(",")) {
                  base64String = base64String.split(",")[1];
                }
                const binaryString = atob(base64String);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                blob = new Blob([bytes], { type: "image/png" });
              } else {
                // Blob URL
                const res = await fetch(url);
                blob = await res.blob();
              }

              // Upload directly to ERP (bypass backend API)
              const formData = new FormData();
              formData.append(
                "images[]",
                blob,
                `shopify-image-${Date.now()}.png`,
              );

              const uploadRes = await fetch(
                "https://erp.thriftops.com/Upload/appImages.php",
                {
                  method: "POST",
                  body: formData,
                },
              );

              if (!uploadRes.ok) {
                throw new Error(`ERP upload failed: ${uploadRes.status}`);
              }

              const data = await uploadRes.json();
              const hostedUrl = Array.isArray(data)
                ? data[0]
                : (data.images && data.images[0]) ||
                  (data.data && data.data[0]);

              console.log(`✅ Image uploaded to ERP: ${hostedUrl}`);
              return hostedUrl;
            };

            // 🔥 MAIN LOGIC: Get images and convert if needed
            let imagesToSubmit = getImagesToSubmit();
            console.log("Original images:", imagesToSubmit);

            // Convert each image if it's base64
            const finalImageUrls = [];
            for (let i = 0; i < imagesToSubmit.length; i++) {
              const image = imagesToSubmit[i];

              if (isBase64(image)) {
                console.log(
                  `⚠️ Image ${i + 1} is base64, converting to hosted URL...`,
                );
                const hostedUrl = await convertBase64ToHostedUrl(image);
                if (hostedUrl) {
                  finalImageUrls.push(hostedUrl);
                } else {
                  console.error(`❌ Failed to convert image ${i + 1}`);
                }
              } else if (
                typeof image === "string" &&
                image.startsWith("http") &&
                !image.startsWith("blob:")
              ) {
                console.log(`✅ Image ${i + 1} is already hosted URL`);
                finalImageUrls.push(image);
              } else if (
                typeof image === "string" &&
                image.startsWith("blob:")
              ) {
                console.log(
                  `⚠️ Image ${i + 1} is blob URL, need to convert...`,
                );
                // Convert blob to hosted URL via base64
                const res = await fetch(image);
                const blob = await res.blob();
                const base64 = await new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result);
                  reader.readAsDataURL(blob);
                });
                const hostedUrl = await convertBase64ToHostedUrl(base64);
                if (hostedUrl) {
                  finalImageUrls.push(hostedUrl);
                }
              } else {
                console.error(
                  `❌ Unknown image format for image ${i + 1}:`,
                  typeof image,
                );
              }
            }

            // ✅ ADD SIZE CHART TO PRODUCT IMAGES (if required)
            if (
              productSettings.sizeChartRequired === "required" &&
              sizeChartFile
            ) {
              try {
                console.log("📸 Uploading size chart to ERP...");
                const sizeChartUrls = await uploadImagesToERP([
                  sizeChartFile.previewUrl,
                ]);
                if (sizeChartUrls && sizeChartUrls.length > 0) {
                  finalImageUrls.push(sizeChartUrls[0]);
                  console.log(
                    "✅ Size chart added to product images:",
                    sizeChartUrls[0],
                  );
                }
              } catch (err) {
                console.error("❌ Failed to upload size chart:", err);
              }
            }

            if (finalImageUrls.length === 0) {
              throw new Error(
                "No valid images found! Please upload images first.",
              );
            }
            console.log(`✅ Total hosted URLs ready: ${finalImageUrls.length}`);

            // Prepare metafields
            const metafieldsArray = [];
            if (
              formData.metafields &&
              Object.keys(formData.metafields).length > 0
            ) {
              Object.entries(formData.metafields).forEach(([key, value]) => {
                if (value?.trim()) {
                  const metafieldDef = activeMetafields.find(
                    (mf) => mf.key === key,
                  );
                  metafieldsArray.push({
                    namespace: metafieldDef?.namespace || "custom",
                    key: key,
                    value: value,
                    type: metafieldDef?.type || "single_line_text_field",
                  });
                }
              });
            }

            // Build payload with ONLY hosted URLs
            const payload = {
              product: {
                title: formData.title || "Test Product",
                body_html: formData.description || "<p>Description</p>",
                vendor: formData.brand || "Brand",
                product_type: formData.productType || "Garments",
                variants: [
                  {
                    option1: "Default",
                    price: String(formData.price || "1.00"),
                    sku: formData.sku || `SKU-${Date.now()}`,
                    inventory_quantity: parseInt(formData.quantity || 1),
                    inventory_management: "shopify",
                  },
                ],
                // ✅ SIRF HOSTED URLS BHEJENGE
                images: finalImageUrls.map((url) => ({
                  src: url,
                  alt: "Product Image",
                })),
                metafields: metafieldsArray,
              },
            };

            // Final check - ensure no base64 in payload
            const payloadStr = JSON.stringify(payload);
            if (
              payloadStr.includes("data:image") ||
              payloadStr.includes("iVBORw0KGgo")
            ) {
              console.error("❌ Base64 still detected in payload!");
              throw new Error("Base64 images detected even after conversion");
            }

            console.log(`📦 Payload size: ~${payloadStr.length} bytes`);
            console.log(`📸 Sending images:`, finalImageUrls);

            const res = await fetch(
              `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/shopify/create/${companyId}`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
              },
            );

            const data = await res.json();
            console.log("Shopify Response:", data);

            if (productFlowLogger) {
              await productFlowLogger.logApiCall({
                company_id: companyId,
                service_name: "SHOPIFY",
                external_service: "SHOPIFY",
                endpoint: `/api/shopify/create/${companyId}`,
                method: "POST",
                request: payload,
                response: data,
                status_code: res.status,
                duration_ms: null,
                step: "CROSS_LISTING",
              });
            }

            const isSuccess =
              res.ok || (data && data.data && data.data.product);

            if (isSuccess) {
              return {
                marketplace: "shopify",
                success: true,
                data: data,
                warning: !res.ok
                  ? "Product created but API returned error status"
                  : undefined,
              };
            } else {
              throw new Error(
                data.message || data.error || "Shopify upload failed",
              );
            }
          } catch (err) {
            console.error("Shopify Error:", err);
            shopifyStatus = "FAILED";
            await safeLogError("CROSS_LISTING_SHOPIFY", err, {
              request_payload: { company_id: companyId },
            });
            return {
              marketplace: "shopify",
              success: false,
              error: err.message,
            };
          } finally {
            await endStepTimer(shopifyTimer, {
              step: "CROSS_LISTING",
              operation: "SHOPIFY_LISTING",
              service_name: "SHOPIFY",
              status: shopifyStatus,
            });
          }
        })();

        marketplacePromises.push(shopifyPromise);
      }

      // ========== EBAY ==========
      if (selectedMps.includes("ebay")) {
        const ebayPromise = (async () => {
          const ebayTimer = startStepTimer("CROSS_LISTING_EBAY");
          let ebayStatus = "SUCCESS";
          try {
            setSubmissionStep("🏷️ Processing eBay...");

            // Convert blob URLs to hosted URLs for eBay
            const urlToHostedUrl = async (url) => {
              if (
                typeof url === "string" &&
                url.startsWith("http") &&
                !url.startsWith("blob:")
              ) {
                return url;
              }

              try {
                const res = await fetch(url);
                const blob = await res.blob();

                const base64 = await new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    const base64String = reader.result.split(",")[1];
                    resolve(base64String);
                  };
                  reader.onerror = reject;
                  reader.readAsDataURL(blob);
                });

                const uploadRes = await fetch(
                  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/image/upload",
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      image: base64,
                      filename: `ebay-image-${Date.now()}.png`,
                    }),
                  },
                );

                if (uploadRes.ok) {
                  const uploadData = await uploadRes.json();
                  return (
                    uploadData.url ||
                    uploadData.imageUrl ||
                    uploadData.image_url
                  );
                } else {
                  console.warn(
                    "Image upload endpoint not available, using blob URL",
                  );
                  return null;
                }
              } catch (err) {
                console.error("Error converting image URL:", err);
                return null;
              }
            };
            // eBay section mein imagesToProcess ke upar
            console.log("🔍 processedImages:", processedImages.length);
            console.log("🔍 editedImages:", editedImages.length);
            console.log("🔍 editedImages values:", editedImages);

            // ✅ FIX
            const imagesToProcess =
              processedImages.length > 0
                ? processedImages
                : editedImages.length > 0
                  ? editedImages
                  : formData.uploadedImages;
            const imageUrls = [];

            for (let i = 0; i < Math.min(imagesToProcess.length, 10); i++) {
              const url = imagesToProcess[i];
              if (
                typeof url === "string" &&
                url.startsWith("http") &&
                !url.startsWith("blob:")
              ) {
                imageUrls.push(url);
              } else {
                const hostedUrl = await urlToHostedUrl(url);
                if (hostedUrl) {
                  imageUrls.push(hostedUrl);
                }
              }
            }

            const finalImageUrls =
              imageUrls.length > 0
                ? imageUrls
                : ["https://via.placeholder.com/500"];

            console.log("eBay image URLs:", finalImageUrls);
            const isAuction = formData.listingType === "AUCTION";
            // const ebayPayload = {
            //   SKU: formData.sku || `SKU-${Date.now()}`,
            //   days: 0,
            //   marketplace_id: "EBAY_US",
            //   email: "test@example.com",
            //   categoryId: formData.categoryId || "177837",
            //   InventoryItem: {
            //     availability: {
            //       shipToLocationAvailability: {
            //         quantity: Number(formData.quantity) || 1,
            //       },
            //     },
            //     condition: (formData.condition || "NEW").toUpperCase(),
            //     packageWeightAndSize: {
            //       dimensions: {
            //         length: "10",
            //         width: "8",
            //         height: "2",
            //         unit: "INCH",
            //       },
            //       weight: {
            //         value: "1",
            //         unit: "POUND",
            //       },
            //     },
            //     product: {
            //       title: formData.title || "Product",
            //       description: formData.description || "No description",
            //       imageUrls: finalImageUrls || [],
            //       aspects: buildEbayAspects(formData.allCategoryAspects || []),
            //     },
            //   },
            //   // offerDetails: {
            //   //   pricingSummary: {
            //   //     price: {
            //   //       value: String(formData.price || "1.00"),
            //   //       currency: "USD",
            //   //     },
            //   //   },
            //   //   merchantLocationKey:
            //   //     savedEbayPolicies?.data?.location || "Warehouse1",
            //   //   listingPolicies: {
            //   //     fulfillmentPolicyId:
            //   //       savedEbayPolicies?.data?.shipping_policy_id || "",
            //   //     paymentPolicyId:
            //   //       savedEbayPolicies?.data?.payment_policy_id || "",
            //   //     returnPolicyId:
            //   //       savedEbayPolicies?.data?.return_policy_id || "",
            //   //   },
            //   // },
            //   offerDetails: {
            //     pricingSummary: isAuction
            //       ? {
            //           auctionStartPrice: {
            //             value: String(formData.price || "1.00"),
            //             currency: "USD",
            //           },
            //         }
            //       : {
            //           price: {
            //             value: String(formData.price || "1.00"),
            //             currency: "USD",
            //           },
            //         },

            //     merchantLocationKey:
            //       savedEbayPolicies?.data?.location || "Warehouse1",

            //     listingPolicies: {
            //       fulfillmentPolicyId:
            //         savedEbayPolicies?.data?.shipping_policy_id || "",
            //       paymentPolicyId:
            //         savedEbayPolicies?.data?.payment_policy_id || "",
            //       returnPolicyId:
            //         savedEbayPolicies?.data?.return_policy_id || "",
            //     },
            //   },
            // };
            const ebayPayload = {
              SKU: formData.sku || `SKU-${Date.now()}`,
              days: 0,
              marketplace_id: "EBAY_GB",
              email: "test@example.com",
              categoryId: formData.categoryId || "177837",
              InventoryItem: {
                availability: {
                  shipToLocationAvailability: {
                    quantity: Number(formData.quantity) || 1,
                  },
                },

                condition: (formData.condition || "NEW").toUpperCase(),
                packageWeightAndSize: {
                  dimensions: {
                    length: "10",
                    width: "8",
                    height: "2",
                    unit: "INCH",
                  },
                  weight: {
                    value: "1",
                    unit: "POUND",
                  },
                },
                product: {
                  title: formData.title || "Product",
                  description: formData.description || "No description",
                  imageUrls: finalImageUrls || [],
                  aspects: buildEbayAspects(formData.categoryAspects || []),
                },
              },
              offerDetails: {
                pricingSummary: isAuction
                  ? {
                      auctionStartPrice: {
                        value: String(formData.price || "1.00"),
                        currency: "GBP",
                      },
                    }
                  : {
                      price: {
                        value: String(formData.price || "1.00"),
                        currency: "GBP",
                      },
                    },

                merchantLocationKey:
                  savedEbayPolicies?.data?.location || "Warehouse1",

                listingPolicies: {
                  fulfillmentPolicyId:
                    savedEbayPolicies?.data?.shipping_policy_id || "",
                  paymentPolicyId:
                    savedEbayPolicies?.data?.payment_policy_id || "",
                  returnPolicyId:
                    savedEbayPolicies?.data?.return_policy_id || "",
                },
              },
            };
            console.log(ebayPayload);

            // const ebayRes = await fetch(
            //   `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/uploadProductToEbayThriftops/${companyId}`,
            //   {
            //     method: "PUT",
            //     headers: { "Content-Type": "application/json" },
            //     body: JSON.stringify(ebayPayload),
            //   },
            // );

            const endpoint =
              formData.listingType === "AUCTION"
                ? `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/uploadProductToEbayThriftopsAuction/${companyId}`
                : `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/uploadProductToEbayThriftops/${companyId}`;

            const ebayRes = await fetch(endpoint, {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(ebayPayload),
            });

            const ebayData = await ebayRes.json();
            if (productFlowLogger) {
              await productFlowLogger.logApiCall({
                company_id: companyId,
                service_name: "EBAY",
                external_service: "EBAY",
                endpoint,
                method: "PUT",
                request: ebayPayload,
                response: ebayData,
                status_code: ebayRes.status,
                duration_ms: null,
                step: "CROSS_LISTING",
              });
            }

            if (ebayRes.ok) {
              // ✅ Save to database after successful eBay upload
              console.log("✅ eBay upload successful, saving to database...");

              const ebayInsertData = {
                product_id: null,
                ebay_item_id:
                  ebayData.publishOffer?.listingId ||
                  ebayData.draftOffer?.offerId,
                sku: formData.sku || `SKU-${Date.now()}`,
                title: formData.title || "Product",
                description: formData.description || "No description",
                aspects: JSON.stringify(
                  buildEbayAspects(formData.categoryAspects || []),
                ),
                condition: (formData.condition || "NEW").toUpperCase(),
                conditionDescription: formData.condition_summary || null,
                quantity: parseInt(formData.quantity) || 1,
                category_id: formData.categoryId || "177837",
                price: parseFloat(formData.price) || 1.0,
                currency: "EUR",
                days: 0,
                ebay_store_id: parseInt(companyId),
              };

              console.log("📝 eBay insert data:", ebayInsertData);

              const insertResponse = await fetch(
                "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/product/ebay-product-insert",
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(ebayInsertData),
                },
              );

              const insertResult = await insertResponse.json();

              if (insertResult.success) {
                console.log(
                  "✅ eBay product saved to DB! ID:",
                  insertResult.insertId,
                );
              } else {
                console.warn("⚠️ eBay DB insert failed:", insertResult.error);
              }

              return {
                marketplace: "ebay",
                success: true,
                data: ebayData,
                dbInsertId: insertResult.insertId,
              };
            } else {
              throw new Error(ebayData.message || "eBay upload failed");
            }
          } catch (err) {
            console.error("eBay Error:", err);
            ebayStatus = "FAILED";
            await safeLogError("CROSS_LISTING_EBAY", err, {
              request_payload: { company_id: companyId },
            });
            return {
              marketplace: "ebay",
              success: false,
              error: err.message,
            };
          } finally {
            await endStepTimer(ebayTimer, {
              step: "CROSS_LISTING",
              operation: "EBAY_LISTING",
              service_name: "EBAY",
              status: ebayStatus,
            });
          }
        })();

        marketplacePromises.push(ebayPromise);
      }

      // ========== POSHMARK ==========
      if (selectedMps.includes("poshmark")) {
        const poshmarkPromise = (async () => {
          const poshmarkTimer = startStepTimer("CROSS_LISTING_POSHMARK");
          let poshmarkStatus = "SUCCESS";
          try {
            setSubmissionStep("👗 Processing Poshmark...");
            const poshmarkResult = await uploadToPoshmark();

            // Check if user action is required
            if (!poshmarkResult.success && poshmarkResult.requiresUserAction) {
              return {
                marketplace: "poshmark",
                success: false,
                error: poshmarkResult.error,
                requiresUserAction: true,
              };
            }

            return {
              marketplace: "poshmark",
              success: poshmarkResult.success,
              data: poshmarkResult.data,
              error: poshmarkResult.error,
            };
          } catch (err) {
            poshmarkStatus = "FAILED";
            await safeLogError("CROSS_LISTING_POSHMARK", err);
            return {
              marketplace: "poshmark",
              success: false,
              error: err.message,
            };
          } finally {
            await endStepTimer(poshmarkTimer, {
              step: "CROSS_LISTING",
              operation: "POSHMARK_LISTING",
              service_name: "POSHMARK",
              status: poshmarkStatus,
            });
          }
        })();

        marketplacePromises.push(poshmarkPromise);
      }

      // ========== VINTED ==========
      // ========== VINTED ==========
      if (selectedMps.includes("vinted")) {
        const vintedPromise = (async () => {
          const vintedTimer = startStepTimer("CROSS_LISTING_VINTED");
          let vintedStatus = "SUCCESS";
          try {
            setSubmissionStep("👕 Processing Vinted...");
            const vintedResult = await uploadToVinted();

            // Agar product ID mil gaya to database mein save ho chuka hai
            if (vintedResult.success) {
              console.log(`✅ Vinted Product ID: ${vintedResult.productId}`);

              // Yahan aap product ID use kar sakte hain
              // Jaise state mein store karna, etc.

              return {
                marketplace: "vinted",
                success: true,
                data: vintedResult.data,
                productId: vintedResult.productId,
                productUrl: vintedResult.productUrl,
              };
            }

            // Check if user action is required (extension not connected)
            if (!vintedResult.success && vintedResult.requiresUserAction) {
              return {
                marketplace: "vinted",
                success: false,
                error: vintedResult.error,
                requiresUserAction: true,
                message: "Extension not connected - User action required",
              };
            }

            // Timeout case
            if (!vintedResult.success && vintedResult.timeout) {
              return {
                marketplace: "vinted",
                success: false,
                error: vintedResult.error,
                timeout: true,
                message: "Upload may have completed but ID not received",
              };
            }

            return {
              marketplace: "vinted",
              success: false,
              error: vintedResult.error,
            };
          } catch (err) {
            console.error("❌ Vinted Promise Error:", err);
            vintedStatus = "FAILED";
            await safeLogError("CROSS_LISTING_VINTED", err);
            return {
              marketplace: "vinted",
              success: false,
              error: err.message,
            };
          } finally {
            await endStepTimer(vintedTimer, {
              step: "CROSS_LISTING",
              operation: "VINTED_LISTING",
              service_name: "VINTED",
              status: vintedStatus,
            });
          }
        })();

        marketplacePromises.push(vintedPromise);
      }

      // Execute all marketplace uploads in parallel with a single progress message
      setSubmissionStep(
        `📤 Uploading to ${marketplacePromises.length} marketplace(s)...`,
      );
      if (productFlowLogger) {
        await productFlowLogger.logCrossListing(selectedMps);
      }

      // Use Promise.allSettled to handle both success and failures without stopping
      const settledResults = await Promise.allSettled(marketplacePromises);

      // Track results for final summary
      const successful = [];
      const failed = [];
      const pending = []; // For marketplaces that need user action

      // Process results
      settledResults.forEach((result) => {
        if (result.status === "fulfilled") {
          results.push(result.value);

          if (result.value.success) {
            successful.push(result.value.marketplace);

            // Show appropriate success message
            if (result.value.marketplace === "poshmark") {
              showSuccess(
                "✅ Poshmark: Automation started! Check your browser tab.",
              );
            } else if (result.value.marketplace === "vinted") {
              showSuccess(
                "✅ Vinted: Automation Started!\n\n" +
                  "📋 What's happening:\n" +
                  "• Vinted tab will activate\n" +
                  "• Form will be filled automatically\n" +
                  "• You'll see progress notifications\n" +
                  "• Item will be uploaded in 30-60 seconds\n\n" +
                  "👀 Watch the Vinted tab!",
              );
            } else if (result.value.marketplace === "shopify") {
              showSuccess("✅ Shopify: Product created successfully!");
            } else if (result.value.marketplace === "ebay") {
              showSuccess("✅ eBay: Listing created successfully!");
            }
          } else {
            // Check if this is a user-action-required case
            if (result.value.requiresUserAction) {
              pending.push(result.value.marketplace);
              // Store the error message for summary
              pendingActions.push({
                marketplace: result.value.marketplace,
                error: result.value.error,
              });
            } else {
              failed.push(`${result.value.marketplace}: ${result.value.error}`);
              showError(
                `❌ ${result.value.marketplace} failed: ${result.value.error}`,
              );
            }
          }
        } else {
          // Handle promise rejection
          failed.push(
            `Unknown marketplace: ${result.reason?.message || "Unknown error"}`,
          );
          showError(`❌ A marketplace upload failed unexpectedly`);
        }
      });

      // ========== CHECK RESULTS ==========
      const successfulMarketplaces = results.filter((r) => r && r.success);

      // If no marketplaces succeeded and no pending actions, throw error
      if (successfulMarketplaces.length === 0 && pending.length === 0) {
        throw new Error("All selected marketplace uploads failed");
      }

      // ========== CREATE PRODUCT IN DATABASE (only if at least one succeeded) ==========
      let productId = null;

      // ========== CREATE PRODUCT IN DATABASE ==========
      if (successfulMarketplaces.length > 0) {
        setSubmissionStep("💾 Creating product in database...");

        const createProductData = await saveProductToDatabase("Active");
        productId = createProductData.product_id;

        // ========== INSERT MARKETPLACE RECORDS ==========
        setSubmissionStep("📝 Saving marketplace records...");

        // Create promises for marketplace record insertion
        const recordPromises = [];

        for (const mp of successfulMarketplaces) {
          if (mp.marketplace === "ebay") {
            recordPromises.push(
              fetch("/api/ebay-product-insert", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  product_id: productId,
                  ebay_item_id:
                    mp.data.publishOffer?.listingId ||
                    mp.data.draftOffer?.offerId,
                  sku: formData.sku,
                  title: formData.title,
                  description: formData.description,
                  quantity: Number(formData.quantity),
                  price: Number(formData.price),
                  currency: "POUND",
                  category_id: formData.categoryId,
                  ebay_store_id: companyId,
                }),
              }),
            );
          }

          if (mp.marketplace === "shopify") {
            console.log("Shopify response:", mp.data?.data);
            const product = mp.data?.data;
            const variant = product?.variants?.[0];

            const metafieldsArray = [];
            if (
              formData.metafields &&
              Object.keys(formData.metafields).length > 0
            ) {
              Object.entries(formData.metafields).forEach(([key, value]) => {
                if (value?.trim()) {
                  const metafieldDef = activeMetafields.find(
                    (mf) => mf.key === key,
                  );
                  metafieldsArray.push({
                    namespace: metafieldDef?.namespace || "custom",
                    key: key,
                    value: value,
                    type: metafieldDef?.type || "single_line_text_field",
                  });
                }
              });
            }

            recordPromises.push(
              fetch(
                "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/shopify-product-insert",
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    product_id: productId,
                    shopify_product_id: product?.id,
                    shopify_variant_id: variant?.id,
                    inventory_item_id: variant?.inventory_item_id,
                    sku: variant?.sku,
                    barcode: variant?.barcode || null,
                    product_type: product?.product_type,
                    vendor: product?.vendor,
                    price: variant?.price,
                    compare_at_price: variant?.compare_at_price,
                    quantity: variant?.inventory_quantity,
                    tags: product?.tags || [],
                    variants: product?.variants || [],
                    metafields: metafieldsArray,
                    shopify_store_id: companyId,
                  }),
                },
              ),
            );
          }
        }

        // Execute all record insertions in parallel
        if (recordPromises.length > 0) {
          await Promise.allSettled(recordPromises);
        }
      }

      // ========== FINAL SUMMARY ==========
      setSubmissionStep("✅ Finalizing...");
      if (productFlowLogger) {
        await productFlowLogger.completeSuccess({ entityId: productId });
      }
      await endStepTimer(crossListingTimer, {
        step: "CROSS_LISTING",
        operation: "ALL_MARKETPLACES",
        service_name: "ADD_PRODUCT_PAGE",
        status: "SUCCESS",
      });

      // Build summary message
      let summaryMessage = "";

      if (successful.length > 0) {
        summaryMessage += `✅ Successful: ${successful.length}\n`;
        summaryMessage +=
          successful
            .map((m) => `   • ${m.charAt(0).toUpperCase() + m.slice(1)}`)
            .join("\n") + "\n\n";
      }

      if (pending.length > 0) {
        summaryMessage += `⚠️ Need Action: ${pending.length}\n`;
        summaryMessage +=
          pending
            .map((m) => `   • ${m.charAt(0).toUpperCase() + m.slice(1)}`)
            .join("\n") + "\n\n";

        // Add specific instructions for Vinted
        if (pending.includes("vinted")) {
          summaryMessage += "📋 Vinted Instructions:\n";
          summaryMessage += "   1. Open Vinted website in Chrome\n";
          summaryMessage += "   2. Go to create listing page\n";
          summaryMessage += "   3. Refresh the page\n";
          summaryMessage +=
            "   4. Wait for 'Ready for Automation' notification\n";
          summaryMessage += "   5. Click 'Create Listing' again\n\n";
        }

        // Add specific instructions for Poshmark
        if (pending.includes("poshmark")) {
          summaryMessage += "📋 Poshmark Instructions:\n";
          summaryMessage += "   1. Open Poshmark website\n";
          summaryMessage += "   2. Go to create listing page\n";
          summaryMessage += "   3. Refresh the page\n";
          summaryMessage += "   4. Click 'Create Listing' again\n\n";
        }
      }

      if (failed.length > 0) {
        summaryMessage += `❌ Failed: ${failed.length}\n`;
        summaryMessage += failed.map((f) => `   • ${f}`).join("\n");
      }

      setSubmissionSuccess(true);

      setTimeout(() => {
        if (failed.length === 0 && pending.length === 0) {
          // All successful
          showSuccess(
            `✅ All Done!\n\n` +
              `${successful.length} marketplace(s) processed successfully.\n\n` +
              `Check your browser tabs for automation in progress.`,
          );

          // Reload after 2 seconds
          setTimeout(() => {
            localStorage.removeItem("appState_addProduct");
            window.location.reload();
          }, 2000);
        } else if (successful.length > 0 && pending.length > 0) {
          // Some succeeded, some need action
          showWarning(summaryMessage);
          setIsSubmitting(false);
        } else if (successful.length === 0 && pending.length > 0) {
          // All need action
          showWarning(summaryMessage);
          setIsSubmitting(false);
        } else {
          // Some failed
          showWarning(summaryMessage);
          setIsSubmitting(false);
        }
      }, 1000);

      // Try to focus Vinted tab if it was successful
      // Try to focus Vinted tab if it was successful
      if (successful.includes("vinted")) {
        // Find the successful Vinted result
        const vintedResult = results.find(
          (r) => r.marketplace === "vinted" && r.success,
        );

        if (vintedResult && vintedResult.productId) {
          console.log(`🎯 Vinted Product ID: ${vintedResult.productId}`);

          // Yahan aap product ID ke saath kuch bhi kar sakte hain
          // Jaise analytics bhejna, notification dena, etc.

          showSuccess(
            `✅ Vinted Success!\n\n` +
              `Product ID: ${vintedResult.productId}\n` +
              `Saved to database automatically.`,
          );
        }

        try {
          if (typeof chrome !== "undefined" && chrome.tabs) {
            chrome.tabs.query({ url: "*://*.vinted.com/*" }, (tabs) => {
              if (tabs && tabs.length > 0) {
                chrome.tabs.update(tabs[0].id, { active: true });
              }
            });
          }
        } catch (e) {
          console.log("Could not focus Vinted tab:", e);
        }
      }

      // Try to focus Poshmark tab if it was successful
      if (successful.includes("poshmark")) {
        try {
          if (typeof chrome !== "undefined" && chrome.tabs) {
            chrome.tabs.query({ url: "*://*.poshmark.com/*" }, (tabs) => {
              if (tabs && tabs.length > 0) {
                chrome.tabs.update(tabs[0].id, { active: true });
              }
            });
          }
        } catch (e) {
          console.log("Could not focus Poshmark tab:", e);
        }
      }
    } catch (err) {
      console.error("❌ Global Error:", err);
      await safeLogError("CROSS_LISTING", err);
      const productFlowLogger = getProductFlowLogger();
      if (productFlowLogger) {
        await productFlowLogger.completeFailed({ error: err.message });
      }
      await endStepTimer(crossListingTimer, {
        step: "CROSS_LISTING",
        operation: "ALL_MARKETPLACES",
        service_name: "ADD_PRODUCT_PAGE",
        status: "FAILED",
      });
      showError(`❌ Error: ${err.message || "Something went wrong"}`);
      setIsSubmitting(false);
    }
  };

  const handleExportCSV = () => {
    if (!formData) return;
    const headers = [
      "Marketplace",
      "Title",
      "Description",
      "SKU",
      "Price",
      "Quantity",
      "Brand",
      "Category",
      "Condition",
      "Color",
      "Variants",
    ];
    const rows = [];
    rows.push([
      "All Marketplaces",
      formData.title,
      formData.description,
      formData.sku,
      isOwnerOrAdmin
        ? formData.price
        : applyDisplayPrice(formData.price, displayPricePercentage),
      formData.quantity,
      formData.brand,
      formData.category,
      formData.condition,
      formData.color,
      formData.variants
        .map((v) => `Size: ${v.size}, Color: ${v.color}, SKU: ${v.sku}`)
        .join(" | "),
    ]);
    formData.selectedMarketplaces.forEach((id) => {
      let marketplaceName = marketplaces.find((m) => m.id === id)?.name || id;
      let row = [
        marketplaceName,
        formData.title,
        formData.description,
        formData.sku,
        isOwnerOrAdmin
          ? formData.price // ← actual price
          : applyDisplayPrice(formData.price, displayPricePercentage),
        formData.quantity,
        formData.brand,
        formData.category,
        formData.condition,
        formData.color,
        formData.variants
          .map((v) => `Size: ${v.size}, Color: ${v.color}, SKU: ${v.sku}`)
          .join(" | "),
      ];
      rows.push(row);
    });
    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.map((v) => `"${v}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "product_listing.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleSwapImages = (first, second) => {
    if (processedImages.length > 0) {
      const items = [...processedImages];
      [items[first], items[second]] = [items[second], items[first]];
      setProcessedImages(items);
    } else {
      const items = [...editedImages];
      [items[first], items[second]] = [items[second], items[first]];
      setEditedImages(items);
    }
  };
  return (
    <>
      {isAutoFilling && (
        <AutoFillLoader message={autoFillMessages[autoFillMessageIndex]} />
      )}
      {isSubmitting && (
        <SubmissionLoader step={submissionStep} isSuccess={submissionSuccess} />
      )}
      {/* <Joyride
        steps={stepstofollow}
        run={run}
        stepIndex={stepIndex}
        callback={handleJoyrideCallback}
        continuous={false}
        showSkipButton={false}
        hideCloseButton={true}
        disableCloseOnEsc={true}
        spotlightClicks={true}
        styles={{
          options: {
            overlayColor: "rgba(0,0,0,0.85)",
            zIndex: 10000,
          },
        }}
      /> */}
      <MDBContainer fluid className="add-product-wizard">
        <div className="wizard-container">
          <div className="wizard-header">
            <h1 className="wizard-title">Add Product</h1>
            <p className="wizard-subtitle">
              Create a new product listing in 5 simple steps
            </p>
          </div>

          <div className="stepper">
            {steps.map((step, index) => (
              <div key={step.id} className="step-container">
                <div
                  className={`step ${currentStep >= step.id ? "active" : ""} ${
                    currentStep === step.id ? "current" : ""
                  } ${isStepClickable(step.id) ? "clickable" : "disabled"}`}
                >
                  <div
                    className="step-number"
                    onClick={() => goToStep(step.id)}
                    style={{
                      cursor: isStepClickable(step.id) ? "pointer" : "default",
                    }}
                  >
                    {currentStep > step.id ? "✓" : step.id}
                  </div>
                  <div className="step-label">{step.title}</div>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`step-connector ${
                      currentStep > step.id ? "completed" : ""
                    }`}
                  ></div>
                )}
              </div>
            ))}
          </div>

          <div className="step-content">
            {currentStep === 1 && (
              <ImageUploadStep
                formData={formData}
                blurTextIndex={blurTextIndex}
                stepIndex={stepIndex}
                moveNext={moveNext}
                handleFileUpload={handleFileUpload}
                handleDragOver={handleDragOver}
                handleDragDrop={handleDragDrop}
                removeImage={removeImage}
                rotateImage={rotateImage} // Right rotate
                rotateImageLeft={rotateImageLeft} // Left rotate
                sizeChartRequired={productSettings.sizeChartRequired}
                sizeChartFile={sizeChartFile}
                sizeChartError={sizeChartError}
                onSizeChartUpload={handleSizeChartUpload}
              />
            )}

            {currentStep === 2 && (
              <ListingDetailsStep
                formData={formData}
                processingImages={processingImages}
                progress={progress}
                blurTextIndex={blurTextIndex}
                loadingAspects={loadingAspects}
                activeMetafields={activeMetafields}
                loadingMetafields={loadingMetafields}
                handleAutoFill={handleAutoFill}
                handleInputChange={handleInputChange}
                handleAspectChange={handleAspectChange}
                handleVariantChange={handleVariantChange}
                addVariant={addVariant}
                removeVariant={removeVariant}
                displayPricePercentage={displayPricePercentage}
                sizeChartRequired={productSettings.sizeChartRequired}
                sizeChartFile={sizeChartFile}
                onSizeChartUpload={handleSizeChartUpload}
                sizeChartError={sizeChartError}
                settingsLoaded={settingsLoaded}
              />
            )}

            {currentStep === 3 && (
              <MarketplacesStep
                formData={formData}
                processingImages={processingImages}
                progress={progress}
                ebayConnected={ebayConnected}
                checkingEbay={checkingEbay}
                poshmarkConnected={poshmarkConnected}
                checkingPoshmark={checkingPoshmark}
                poshmarkSubCategories={poshmarkSubCategories}
                vintedConnected={vintedConnected}
                checkingVinted={checkingVinted}
                vintedMainCategories={vintedMainCategories}
                vintedSubCategories={vintedSubCategories}
                vintedCategoryHistory={vintedCategoryHistory}
                vintedCurrentCategoryId={vintedCurrentCategoryId}
                vintedSelectedCategoryPath={vintedSelectedCategoryPath}
                vintedLoadingCategories={vintedLoadingCategories}
                handleMarketplaceToggle={handleMarketplaceToggle}
                handlePoshmarkIntegrate={handlePoshmarkIntegrate}
                handlePoshmarkCategoryChange={handlePoshmarkCategoryChange}
                handleVintedIntegrate={handleVintedIntegrate}
                handleVintedCategoryClick={handleVintedCategoryClick}
                handleVintedBack={handleVintedBack}
                resetVintedCategory={resetVintedCategory}
                handleInputChange={handleInputChange}
                handleVariantChange={handleVariantChange}
                 vintedDescription={formData.vintedDescription}
  onVintedDescriptionChange={(val) => handleInputChange("vintedDescription", val)}
              />
            )}

            {currentStep === 4 && (
              <ImageProcessingStep
                formData={formData}
                processing={processing}
                processingImages={processingImages}
                progress={progress}
                processedImages={processedImages}
                editedImages={editedImages} // ← ADD
                handleSwapImages={handleSwapImages}
                handleInputChange={handleInputChange}
                handleStartImageEditing={handleStartImageEditing}
                setProcessedImages={setProcessedImages}
                setEditedImages={setEditedImages} // ← ADD
                rotateProcessedImageRight={rotateProcessedImageRight}
                rotateProcessedImageLeft={rotateProcessedImageLeft}
              />
            )}

            {currentStep === 5 && (
              <ReviewStep
                formData={formData}
                processedImages={processedImages}
                poshmarkConnected={poshmarkConnected}
                vintedConnected={vintedConnected}
                getImagesToSubmit={getImagesToSubmit}
                displayPricePercentage={displayPricePercentage}
                sizeChartFile={sizeChartFile}
                sizeChartRequired={productSettings.sizeChartRequired}
              />
            )}
          </div>

          {currentStep === 2 &&
            formData.condition &&
            !isProductConditionAcceptable() && (
              <p className="text-danger text-center mb-3">
                Product condition is not acceptable
              </p>
            )}

          <div className="wizard-navigation">
            <MDBBtn
              color="light"
              disabled={currentStep === 1}
              onClick={prevStep}
              className="nav-btn"
            >
              Back
            </MDBBtn>
            {currentStep < steps.length ? (
              <MDBBtn
                className="btn-red"
                disabled={!isStepValid()}
                onClick={nextStep}
                title={
                  currentStep === 2 && !isProductConditionAcceptable()
                    ? "Product condition is not acceptable"
                    : undefined
                }
              >
                Next
              </MDBBtn>
            ) : (
              <>
                {!isSeller && (
                  <MDBBtn
                    className="btn-red"
                    onClick={handleSubmit}
                    disabled={processingImages || isSubmitting}
                  >
                    {isSubmitting ? "Creating..." : "Create Listing"}
                  </MDBBtn>
                )}
                <MDBBtn
                  className={`btn-outline-dark ${!isSeller ? "ms-3" : ""}`}
                  onClick={handleSaveDraft}
                  disabled={processingImages || isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Upload Now"}
                </MDBBtn>
                <MDBBtn
                  className="btn-outline-dark ms-3"
                  onClick={handleExportCSV}
                  disabled={processingImages}
                >
                  Export CSV
                </MDBBtn>
              </>
            )}
          </div>
        </div>
      </MDBContainer>
      <MessageBoxComponent />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default AddProduct;
